import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  LayoutDashboard,
  Sliders,
  Bookmark,
  Bell,
  Clock,
  User,
  Code2,
  Settings,
  HelpCircle,
  Search,
  Plus,
  RefreshCw,
  Banknote,
  Home,
  TrendingUp,
  TrendingDown,
  Percent,
  Receipt,
  ArrowRight,
  ChevronRight,
  ExternalLink,
  ShieldCheck,
  Key,
  Smartphone,
  Laptop,
  Trash2,
  Download,
  LogOut,
  X,
  Sparkles,
  CheckCircle2,
  AlertTriangle,
  Send,
  Eye,
  Lock,
  Calendar,
  Building2,
  SlidersHorizontal,
  ChevronDown,
  Globe,
  Calculator,
  Wallet,
  PieChart,
  PiggyBank,
  CreditCard,
  Tag,
  Star,
  Filter,
  MoreVertical,
  Edit3,
  Copy,
  Archive,
  Scale,
} from 'lucide-react';
import {
  getIdentityState,
  subscribeIdentityState,
  registerPasskey,
  removePasskey,
  revokeSession,
  saveCalculationToAccount,
  renameSavedCalculation,
  duplicateSavedCalculation,
  archiveSavedCalculation,
  deleteSavedCalculation,
  createFinancialScenario,
  deleteFinancialScenario,
  createGlobalAlert,
  toggleAlertStatus,
  deleteGlobalAlert,
  toggleFavorite,
  markNotificationRead,
  markAllNotificationsRead,
  updateNotificationPreferences,
  updatePrivacyPreferences,
  exportUserDataJSON,
  deleteAccountData,
  logout,
  loginWithProvider,
  activateSudoMode,
  isSudoActive,
  updateUserProfile,
  revokeAllOtherSessions,
} from '../../services/identityEngine';
import {
  AppView,
  SavedCalculation,
  FinancialScenario,
  GlobalAlert,
  NotificationItem,
  ToolItem,
  YamooPayActivityItem,
  YamooPayActivityCategory,
} from '../../types';
import { TOOLS_DIRECTORY } from '../../data/toolsData';
import { fxEngine, formatRateDisplay } from '../../services/fxEngine';
import { YamooPayLogo } from '../common/YamooPayLogo';

interface MyYamooPayHubProps {
  onNavigateView?: (view: AppView) => void;
  onSelectTool?: (toolId: string) => void;
  onOpenAuthModal?: () => void;
}

type DashboardTab =
  | 'overview'
  | 'tools'
  | 'saved'
  | 'alerts'
  | 'activity'
  | 'account'
  | 'developer'
  | 'settings'
  | 'support';

export const MyYamooPayHub: React.FC<MyYamooPayHubProps> = ({
  onNavigateView,
  onSelectTool,
  onOpenAuthModal,
}) => {
  const [state, setState] = useState(() => getIdentityState());
  const [activeTab, setActiveTab] = useState<DashboardTab>('overview');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchFocused, setSearchFocused] = useState(false);
  const [toastMsg, setToastMsg] = useState<string | null>(null);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);

  // Modals inside tabs
  const [showAddPasskey, setShowAddPasskey] = useState(false);
  const [passkeyLabel, setPasskeyLabel] = useState('MacBook Touch ID / FIDO Key');
  const [showAddAlert, setShowAddAlert] = useState(false);
  const [alertPair, setAlertPair] = useState('EUR/USD');
  const [alertThreshold, setAlertThreshold] = useState('1.1000');

  // Authenticated Tools Library state
  const [toolsCategory, setToolsCategory] = useState<string>('All');
  const [toolsSearchQuery, setToolsSearchQuery] = useState<string>('');
  const [toolsVisibleCount, setToolsVisibleCount] = useState<number>(6);
  const [toolsFilterFavorites, setToolsFilterFavorites] = useState<boolean>(false);

  // Recent Activity state
  const [activityCategoryFilter, setActivityCategoryFilter] = useState<YamooPayActivityCategory>('ALL');
  const [activityVisibleCount, setActivityVisibleCount] = useState<number>(5);
  const [selectedActivityDetail, setSelectedActivityDetail] = useState<YamooPayActivityItem | null>(null);

  // Filtered Activity items
  const filteredActivities = useMemo(() => {
    const list = state.activities || [];
    if (activityCategoryFilter === 'ALL') return list;
    return list.filter((act) => act.category === activityCategoryFilter);
  }, [state.activities, activityCategoryFilter]);

  // Paginated visible Activity items
  const visibleActivities = useMemo(() => {
    return filteredActivities.slice(0, activityVisibleCount);
  }, [filteredActivities, activityVisibleCount]);

  // Grouped visible activities by TODAY, YESTERDAY, EARLIER
  const groupedActivities = useMemo(() => {
    const groups: { [key: string]: YamooPayActivityItem[] } = {
      TODAY: [],
      YESTERDAY: [],
      EARLIER: [],
    };
    visibleActivities.forEach((item) => {
      if (groups[item.group]) {
        groups[item.group].push(item);
      } else {
        groups.EARLIER.push(item);
      }
    });
    return groups;
  }, [visibleActivities]);

  const getActivityIcon = (type: string) => {
    switch (type) {
      case 'CURRENCY_CONVERSION':
        return <RefreshCw className="w-5 h-5 text-[#0F766E]" />;
      case 'SALARY_CALCULATION':
        return <Calculator className="w-5 h-5 text-[#0F766E]" />;
      case 'MORTGAGE_SCENARIO':
        return <Home className="w-5 h-5 text-[#0F766E]" />;
      case 'SAVED_CALCULATION':
        return <Bookmark className="w-5 h-5 text-[#0F766E]" />;
      case 'FX_ALERT_TRIGGERED':
        return <TrendingUp className="w-5 h-5 text-amber-700" />;
      case 'SECURITY_SETTING_CHANGED':
        return <ShieldCheck className="w-5 h-5 text-slate-700" />;
      case 'DEVELOPER_API_ACTIVITY':
        return <Code2 className="w-5 h-5 text-indigo-700" />;
      default:
        return <Clock className="w-5 h-5 text-[#6B7280]" />;
    }
  };

  const getActivityIconBg = (type: string) => {
    switch (type) {
      case 'CURRENCY_CONVERSION':
      case 'SALARY_CALCULATION':
      case 'MORTGAGE_SCENARIO':
      case 'SAVED_CALCULATION':
        return 'bg-[#E6F4F1]';
      case 'FX_ALERT_TRIGGERED':
        return 'bg-amber-100/70';
      case 'SECURITY_SETTING_CHANGED':
        return 'bg-slate-100';
      case 'DEVELOPER_API_ACTIVITY':
        return 'bg-indigo-50';
      default:
        return 'bg-gray-100';
    }
  };

  // Saved Calculations state
  const [savedCategoryFilter, setSavedCategoryFilter] = useState<
    'All' | 'Currency' | 'Income & Tax' | 'Borrow' | 'Save & Invest' | 'Everyday'
  >('All');
  const [activeKebabId, setActiveKebabId] = useState<string | null>(null);
  const [renamingCalc, setRenamingCalc] = useState<SavedCalculation | null>(null);
  const [renamingTitle, setRenamingTitle] = useState<string>('');
  const [comparingCalc, setComparingCalc] = useState<SavedCalculation | null>(null);

  // Filtered saved calculations
  const filteredSavedCalculations = useMemo(() => {
    const list = state.savedCalculations || [];
    if (savedCategoryFilter === 'All') return list;
    return list.filter((calc) => calc.category === savedCategoryFilter);
  }, [state.savedCalculations, savedCategoryFilter]);

  // Account Settings Secondary Navigation & Forms state
  const [accountSubNav, setAccountSubNav] = useState<
    'overview' | 'profile' | 'preferences' | 'language-region' | 'security' | 'notifications' | 'privacy' | 'support' | 'developer' | 'billing'
  >('overview');

  const [profileFullName, setProfileFullName] = useState(state.user.displayName || 'Alex Morgan');
  const [profileDisplayName, setProfileDisplayName] = useState('AMorgan');
  const [profileLanguage, setProfileLanguage] = useState(state.user.locale === 'fr-FR' ? 'Français' : 'English (US)');
  const [profileTimezone, setProfileTimezone] = useState('(GMT-08:00) Pacific Time (US & Canada)');
  const [profileCountry, setProfileCountry] = useState('United States');
  const [profileCurrency, setProfileCurrency] = useState(state.user.defaultCurrency ? `${state.user.defaultCurrency} ($)` : 'USD ($)');
  const [profileNumberFormat, setProfileNumberFormat] = useState('1,234.56 (MM/DD/YYYY)');

  // Security modals & dialogs
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [currentPasswordInput, setCurrentPasswordInput] = useState('');
  const [newPasswordInput, setNewPasswordInput] = useState('');
  const [confirmPasswordInput, setConfirmPasswordInput] = useState('');

  const [show2FAModal, setShow2FAModal] = useState(false);
  const [twoFactorActive, setTwoFactorActive] = useState(true);
  const [showDeleteAccountModal, setShowDeleteAccountModal] = useState(false);
  const [deleteConfirmationText, setDeleteConfirmationText] = useState('');
  const [showClearCacheModal, setShowClearCacheModal] = useState(false);
  const [developerKeyRevealed, setDeveloperKeyRevealed] = useState(false);
  const [developerWorkspaceActive, setDeveloperWorkspaceActive] = useState(true);
  const [billingPlanActive, setBillingPlanActive] = useState(true);

  const searchInputRef = useRef<HTMLInputElement>(null);
  const toolsSearchInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const unsubscribe = subscribeIdentityState((newState) => {
      setState({ ...newState });
    });
    return () => unsubscribe();
  }, []);

  const showToast = (msg: string) => {
    setToastMsg(msg);
    setTimeout(() => setToastMsg(null), 3000);
  };

  // Dynamic greeting based on current local hour
  const greeting = useMemo(() => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 18) return 'Good afternoon';
    return 'Good evening';
  }, []);

  const firstName = useMemo(() => {
    if (state.user.displayName) {
      return state.user.displayName.split(' ')[0];
    }
    return 'Alex';
  }, [state.user.displayName]);

  // Live calculation reference values for "Continue where you left off"
  const recentFxRate = useMemo(() => {
    const obs = fxEngine.getPairObservation('USD', 'EUR');
    return obs.rate ? formatRateDisplay(obs.rate) : '0.9245';
  }, []);

  // Filtered tools for the search bar
  const searchResults = useMemo(() => {
    const q = searchQuery.toLowerCase().trim();
    if (!q) return [];
    return TOOLS_DIRECTORY.filter(
      (t) =>
        t.name.toLowerCase().includes(q) ||
        t.category.toLowerCase().includes(q) ||
        t.subtitle?.toLowerCase().includes(q) ||
        t.description.toLowerCase().includes(q)
    ).slice(0, 5);
  }, [searchQuery]);

  const handleLaunchTool = (toolId: string) => {
    if (onSelectTool) {
      onSelectTool(toolId);
    } else if (onNavigateView) {
      if (toolId === 'currency-converter') onNavigateView('active-tool');
      else if (toolId === 'salary-engine' || toolId === 'salary-calculator') onNavigateView('salary-engine');
      else if (toolId === 'loan-engine' || toolId === 'mortgage-calculator' || toolId === 'loan-calculator') onNavigateView('loan-engine');
      else if (toolId === 'money-transfers' || toolId === 'compare-transfers') onNavigateView('money-transfers');
      else onNavigateView('tools');
    }
  };

  // Tools Library Categorization & Filtering
  const TOOL_CATEGORIES = useMemo(
    () => [
      'All',
      'Everyday Money',
      'Currency & Transfers',
      'Income & Tax',
      'Borrow',
      'Save & Invest',
    ],
    []
  );

  const isToolFavorite = (toolId: string) => {
    return state.favorites.some((f) => f.type === 'TOOL' && f.targetId === toolId);
  };

  const handleToggleToolFavorite = (e: React.MouseEvent, tool: ToolItem) => {
    e.stopPropagation();
    const wasFav = isToolFavorite(tool.id);
    const meta = getToolDisplayMeta(tool);
    toggleFavorite('TOOL', tool.id, meta.title, meta.description);
    showToast(wasFav ? `Removed ${meta.title} from favorites` : `Saved ${meta.title} to favorites`);
  };

  // Tool display presentation matching exact design system
  const getToolDisplayMeta = (tool: ToolItem) => {
    switch (tool.id) {
      case 'vat-sales-tax':
        return {
          title: 'VAT Calculator',
          description: 'Instantly add or remove Value Added Tax from any amount. Supports custom rates.',
          icon: Receipt,
        };
      case 'salary-calculator':
      case 'salary-engine':
        return {
          title: 'Salary Estimator',
          description: 'Calculate take-home pay after standard deductions and tax brackets.',
          icon: Wallet,
        };
      case 'mortgage-calculator':
        return {
          title: 'Mortgage Comparison',
          description: 'Compare multiple mortgage offers side-by-side to find the best long-term rate.',
          icon: Home,
        };
      case 'currency-converter':
        return {
          title: 'Currency Converter',
          description: 'Real-time exchange rates for over 150 global currencies.',
          icon: RefreshCw,
        };
      case 'split-bill-calculator':
        return {
          title: 'Budget Splitter',
          description: 'Easily divide shared expenses among roommates, partners, or friends.',
          icon: PieChart,
        };
      case 'savings-calculator':
        return {
          title: 'Savings Goal Tracker',
          description: 'Map out a timeline to reach your financial milestones based on contribution rates.',
          icon: PiggyBank,
        };
      case 'loan-calculator':
      case 'loan-engine':
        return {
          title: 'Loan Payment Calculator',
          description: 'Calculate monthly amortization payments, total interest costs, and paydown schedules.',
          icon: CreditCard,
        };
      case 'compound-interest-calculator':
        return {
          title: 'Compound Interest',
          description: 'Visualize compounding growth across monthly deposits and annual yield forecasts.',
          icon: TrendingUp,
        };
      case 'inflation-purchasing-power':
        return {
          title: 'Inflation & Purchasing Power',
          description: 'Calculate real purchasing power loss over time adjusted for CPI inflation.',
          icon: TrendingDown,
        };
      case 'discount-calculator':
        return {
          title: 'Discount & Sale Price',
          description: 'Determine exact retail savings and checkout price after percentage markdowns.',
          icon: Tag,
        };
      case 'percentage-calculator':
        return {
          title: 'Percentage Calculator',
          description: 'Instant percentage increase, decrease, relative change, and proportion calculations.',
          icon: Percent,
        };
      default:
        return {
          title: tool.label || tool.name,
          description: tool.description,
          icon: Calculator,
        };
    }
  };

  // Curated ordering of tools so that the 6 primary tools appear first
  const orderedTools = useMemo(() => {
    const priorityOrder = [
      'vat-sales-tax',
      'salary-calculator',
      'mortgage-calculator',
      'currency-converter',
      'split-bill-calculator',
      'savings-calculator',
    ];

    const sorted = [...TOOLS_DIRECTORY].sort((a, b) => {
      const idxA = priorityOrder.indexOf(a.id);
      const idxB = priorityOrder.indexOf(b.id);
      if (idxA !== -1 && idxB !== -1) return idxA - idxB;
      if (idxA !== -1) return -1;
      if (idxB !== -1) return 1;
      return 0;
    });

    return sorted;
  }, []);

  // Filtered tools based on selected Category, Search Query, and Favorites
  const authenticatedFilteredTools = useMemo(() => {
    return orderedTools.filter((tool) => {
      // 1. Category Filter
      if (toolsCategory !== 'All') {
        if (tool.category !== toolsCategory) return false;
      }

      // 2. Favorites only
      if (toolsFilterFavorites && !isToolFavorite(tool.id)) {
        return false;
      }

      // 3. Search query
      if (toolsSearchQuery.trim()) {
        const q = toolsSearchQuery.toLowerCase().trim();
        const meta = getToolDisplayMeta(tool);
        const matchTitle = meta.title.toLowerCase().includes(q);
        const matchDesc = meta.description.toLowerCase().includes(q);
        const matchName = tool.name.toLowerCase().includes(q);
        const matchCat = tool.category.toLowerCase().includes(q);
        const matchSub = tool.subtitle?.toLowerCase().includes(q) || false;
        if (!matchTitle && !matchDesc && !matchName && !matchCat && !matchSub) {
          return false;
        }
      }

      return true;
    });
  }, [orderedTools, toolsCategory, toolsFilterFavorites, toolsSearchQuery, state.favorites]);

  const visibleTools = useMemo(() => {
    return authenticatedFilteredTools.slice(0, toolsVisibleCount);
  }, [authenticatedFilteredTools, toolsVisibleCount]);

  // Passkey creation handler
  const handleCreatePasskey = async (e: React.FormEvent) => {
    e.preventDefault();
    await registerPasskey(passkeyLabel);
    setShowAddPasskey(false);
    setPasskeyLabel('MacBook Touch ID / FIDO Key');
    showToast('New FIDO2 passkey credential registered successfully');
  };

  // Alert creation handler
  const handleCreateAlert = (e: React.FormEvent) => {
    e.preventDefault();
    createGlobalAlert('EXCHANGE_RATE', alertPair, parseFloat(alertThreshold) || 1.1, 1.092, 'IN_APP');
    setShowAddAlert(false);
    showToast(`Alert created for ${alertPair} at ${alertThreshold}`);
  };

  // Relative time helper
  const getRelativeTime = (timestamp: number | string) => {
    const timeMs = typeof timestamp === 'number' ? timestamp : new Date(timestamp).getTime();
    const diffHours = Math.round((Date.now() - timeMs) / (1000 * 60 * 60));
    if (diffHours < 1) return 'Just now';
    if (diffHours < 24) return `${diffHours} hours ago`;
    const diffDays = Math.round(diffHours / 24);
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.round(diffDays / 7)} weeks ago`;
    return `${Math.round(diffDays / 30)} months ago`;
  };

  const isDeveloper = state.roleAssignments.some(
    (r) => r.role === 'SystemAdmin' || r.role === 'PlatformEngineer' || r.role === 'DataEngineer'
  );

  const unreadNotificationsCount = state.notifications.filter((n) => !n.read).length;

  return (
    <div className="min-h-screen bg-[#FBFBFC] text-[#1A1A1A] flex flex-col md:flex-row font-sans selection:bg-[#E6F4F1] selection:text-[#0F766E]">
      {/* Toast Notification */}
      {toastMsg && (
        <div className="fixed bottom-6 right-6 z-50 px-4 py-3 bg-[#1A1A1A] text-white font-bold text-xs rounded-xl shadow-xl flex items-center gap-2 animate-in slide-in-from-bottom-5 duration-150">
          <CheckCircle2 className="w-4 h-4 text-[#10B981]" />
          <span>{toastMsg}</span>
        </div>
      )}

      {/* =================================================================== */}
      {/* 1. SIDEBAR (DESKTOP ONLY)                                           */}
      {/* =================================================================== */}
      <aside className="hidden md:flex w-64 bg-white border-r border-[#E5E7EB] flex-col shrink-0 md:min-h-screen sticky top-0 z-30">
        {/* Brand Header */}
        <div className="p-5 border-b border-[#F3F4F6] flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-[#0B3B3C] text-white flex items-center justify-center shadow-xs">
              <Building2 className="w-4 h-4 text-emerald-300" />
            </div>
            <div>
              <span className="text-sm font-bold text-[#1A1A1A] tracking-tight block">
                My YamooPay
              </span>
              <span className="text-[11px] font-medium text-[#6B7280] block">
                User Workspace
              </span>
            </div>
          </div>
        </div>

        {/* Navigation Items */}
        <div className="flex-1 p-3 space-y-1">
          {/* Overview */}
          <button
            onClick={() => {
              setActiveTab('overview');
            }}
            className={`w-full flex items-center gap-3 px-3.5 py-2.5 rounded-xl text-xs font-bold transition-all text-left cursor-pointer ${
              activeTab === 'overview'
                ? 'bg-[#E6F4F1] text-[#0F766E]'
                : 'text-[#4B5563] hover:text-[#1A1A1A] hover:bg-[#F9FAFB]'
            }`}
          >
            <LayoutDashboard className={`w-4 h-4 ${activeTab === 'overview' ? 'text-[#0D9488]' : 'text-[#6B7280]'}`} />
            <span>Overview</span>
          </button>

          {/* Tools */}
          <button
            onClick={() => {
              setActiveTab('tools');
            }}
            className={`w-full flex items-center gap-3 px-3.5 py-2.5 rounded-xl text-xs font-bold transition-all text-left cursor-pointer ${
              activeTab === 'tools'
                ? 'bg-[#E6F4F1] text-[#0F766E]'
                : 'text-[#4B5563] hover:text-[#1A1A1A] hover:bg-[#F9FAFB]'
            }`}
          >
            <Sliders className={`w-4 h-4 ${activeTab === 'tools' ? 'text-[#0D9488]' : 'text-[#6B7280]'}`} />
            <span>Tools</span>
          </button>

          {/* Saved */}
          <button
            onClick={() => {
              setActiveTab('saved');
            }}
            className={`w-full flex items-center justify-between px-3.5 py-2.5 rounded-xl text-xs font-bold transition-all text-left cursor-pointer ${
              activeTab === 'saved'
                ? 'bg-[#E6F4F1] text-[#0F766E]'
                : 'text-[#4B5563] hover:text-[#1A1A1A] hover:bg-[#F9FAFB]'
            }`}
          >
            <div className="flex items-center gap-3">
              <Bookmark className={`w-4 h-4 ${activeTab === 'saved' ? 'text-[#0D9488]' : 'text-[#6B7280]'}`} />
              <span>Saved</span>
            </div>
            {state.savedCalculations.length > 0 && (
              <span className="px-2 py-0.5 text-[10px] font-bold rounded-full bg-white/80 border border-[#E5E7EB] text-[#4B5563]">
                {state.savedCalculations.length}
              </span>
            )}
          </button>

          {/* Alerts */}
          <button
            onClick={() => {
              setActiveTab('alerts');
            }}
            className={`w-full flex items-center justify-between px-3.5 py-2.5 rounded-xl text-xs font-bold transition-all text-left cursor-pointer ${
              activeTab === 'alerts'
                ? 'bg-[#E6F4F1] text-[#0F766E]'
                : 'text-[#4B5563] hover:text-[#1A1A1A] hover:bg-[#F9FAFB]'
            }`}
          >
            <div className="flex items-center gap-3">
              <Bell className={`w-4 h-4 ${activeTab === 'alerts' ? 'text-[#0D9488]' : 'text-[#6B7280]'}`} />
              <span>Alerts</span>
            </div>
            {state.alerts.length > 0 && (
              <span className="px-2 py-0.5 text-[10px] font-bold rounded-full bg-[#FEF3C7] text-[#B45309]">
                {state.alerts.length}
              </span>
            )}
          </button>

          {/* Activity */}
          <button
            onClick={() => {
              setActiveTab('activity');
            }}
            className={`w-full flex items-center gap-3 px-3.5 py-2.5 rounded-xl text-xs font-bold transition-all text-left cursor-pointer ${
              activeTab === 'activity'
                ? 'bg-[#E6F4F1] text-[#0F766E]'
                : 'text-[#4B5563] hover:text-[#1A1A1A] hover:bg-[#F9FAFB]'
            }`}
          >
            <Clock className={`w-4 h-4 ${activeTab === 'activity' ? 'text-[#0D9488]' : 'text-[#6B7280]'}`} />
            <span>Activity</span>
          </button>

          {/* Account */}
          <button
            onClick={() => {
              setActiveTab('account');
            }}
            className={`w-full flex items-center gap-3 px-3.5 py-2.5 rounded-xl text-xs font-bold transition-all text-left cursor-pointer ${
              activeTab === 'account'
                ? 'bg-[#E6F4F1] text-[#0F766E]'
                : 'text-[#4B5563] hover:text-[#1A1A1A] hover:bg-[#F9FAFB]'
            }`}
          >
            <User className={`w-4 h-4 ${activeTab === 'account' ? 'text-[#0D9488]' : 'text-[#6B7280]'}`} />
            <span>Account</span>
          </button>

          {/* Conditional: Developer */}
          {isDeveloper && (
            <button
              onClick={() => {
                setActiveTab('developer');
              }}
              className={`w-full flex items-center gap-3 px-3.5 py-2.5 rounded-xl text-xs font-bold transition-all text-left cursor-pointer ${
                activeTab === 'developer'
                  ? 'bg-[#E6F4F1] text-[#0F766E]'
                  : 'text-[#4B5563] hover:text-[#1A1A1A] hover:bg-[#F9FAFB]'
              }`}
            >
              <Code2 className={`w-4 h-4 ${activeTab === 'developer' ? 'text-[#0D9488]' : 'text-[#6B7280]'}`} />
              <span>Developer</span>
            </button>
          )}

          {/* Divider */}
          <div className="pt-4 pb-2">
            <div className="h-[1px] bg-[#F3F4F6]" />
          </div>

          {/* Bottom Nav Items: Help & Profile */}
          <button
            onClick={() => {
              setActiveTab('support');
            }}
            className={`w-full flex items-center gap-3 px-3.5 py-2.5 rounded-xl text-xs font-bold transition-all text-left cursor-pointer ${
              activeTab === 'support'
                ? 'bg-[#E6F4F1] text-[#0F766E]'
                : 'text-[#4B5563] hover:text-[#1A1A1A] hover:bg-[#F9FAFB]'
            }`}
          >
            <HelpCircle className={`w-4 h-4 ${activeTab === 'support' ? 'text-[#0D9488]' : 'text-[#6B7280]'}`} />
            <span>Help</span>
          </button>

          <button
            onClick={() => {
              setActiveTab('account');
            }}
            className={`w-full flex items-center gap-3 px-3.5 py-2.5 rounded-xl text-xs font-bold transition-all text-left cursor-pointer ${
              activeTab === 'account'
                ? 'bg-[#E6F4F1] text-[#0F766E]'
                : 'text-[#4B5563] hover:text-[#1A1A1A] hover:bg-[#F9FAFB]'
            }`}
          >
            <User className={`w-4 h-4 ${activeTab === 'account' ? 'text-[#0D9488]' : 'text-[#6B7280]'}`} />
            <span>Profile</span>
          </button>
        </div>

        {/* User Mini Card in Bottom Sidebar */}
        <div className="p-4 border-t border-[#F3F4F6]">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-[#E6F4F1] text-[#0F766E] font-bold text-xs flex items-center justify-center">
              {firstName[0] || 'A'}
            </div>
            <div className="truncate flex-1 min-w-0">
              <span className="text-xs font-bold text-[#1A1A1A] block truncate">
                {state.user.displayName || 'Alexandre Dubois'}
              </span>
              <span className="text-[11px] text-[#6B7280] block truncate">
                {state.user.primaryEmail}
              </span>
            </div>
          </div>
        </div>
      </aside>

      {/* =================================================================== */}
      {/* 2. MAIN WORKSPACE CONTENT AREA                                      */}
      {/* =================================================================== */}
      <div className="flex-1 flex flex-col min-w-0 overflow-y-auto">
        
        {/* Top Header Utilities (aligned right: New Calculation, Bell, Avatar) */}
        <div className="w-full max-w-6xl mx-auto px-4 sm:px-8 pt-6 pb-2 flex items-center justify-end gap-3">
          <button
            onClick={() => {
              setActiveTab('overview');
              searchInputRef.current?.focus();
            }}
            className="px-4 py-2 bg-[#0066FF] hover:bg-[#0052CC] text-white text-xs font-bold rounded-xl transition-all shadow-xs flex items-center gap-1.5"
          >
            <Plus className="w-4 h-4" />
            <span>New Calculation</span>
          </button>

          {/* Notifications Trigger */}
          <div className="relative">
            <button
              onClick={() => setNotificationsOpen(!notificationsOpen)}
              className="p-2 text-[#6B7280] hover:text-[#1A1A1A] hover:bg-white rounded-xl border border-transparent hover:border-[#E5E7EB] transition-all relative"
              title="Notifications"
            >
              <Bell className="w-4 h-4" />
              {unreadNotificationsCount > 0 && (
                <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-rose-500 rounded-full ring-2 ring-white" />
              )}
            </button>

            {/* Notifications Dropdown */}
            {notificationsOpen && (
              <div
                className="absolute right-0 mt-2 w-80 bg-white border border-[#E5E7EB] rounded-2xl shadow-xl z-50 p-3 space-y-2 animate-in fade-in zoom-in-95 duration-100"
                onMouseLeave={() => setNotificationsOpen(false)}
              >
                <div className="flex items-center justify-between pb-2 border-b border-[#F3F4F6] px-1">
                  <span className="text-xs font-bold text-[#1A1A1A]">Notifications</span>
                  {unreadNotificationsCount > 0 && (
                    <button
                      onClick={() => markAllNotificationsRead()}
                      className="text-[10px] text-[#0F766E] font-bold hover:underline"
                    >
                      Mark all as read
                    </button>
                  )}
                </div>
                <div className="max-h-60 overflow-y-auto space-y-1">
                  {state.notifications.length === 0 ? (
                    <p className="text-xs text-[#9CA3AF] p-4 text-center">No notifications</p>
                  ) : (
                    state.notifications.map((n) => (
                      <div
                        key={n.id}
                        onClick={() => markNotificationRead(n.id)}
                        className={`p-2.5 rounded-xl text-xs transition-colors cursor-pointer ${
                          !n.read ? 'bg-[#F9FAFB] font-semibold text-[#1A1A1A]' : 'text-[#6B7280]'
                        }`}
                      >
                        <div className="text-xs font-bold">{n.title}</div>
                        <div className="text-[11px] opacity-80">{n.message}</div>
                        <div className="text-[9px] text-[#9CA3AF] mt-1">{getRelativeTime(n.timestamp)}</div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Profile Dropdown */}
          <div className="relative">
            <button
              onClick={() => setProfileMenuOpen(!profileMenuOpen)}
              className="p-1 rounded-full border border-[#E5E7EB] hover:border-[#9CA3AF] transition-colors"
              title="User profile"
            >
              <div className="w-7 h-7 rounded-full bg-[#E6F4F1] text-[#0F766E] font-bold text-xs flex items-center justify-center">
                {firstName[0] || 'A'}
              </div>
            </button>

            {profileMenuOpen && (
              <div
                className="absolute right-0 mt-2 w-56 bg-white border border-[#E5E7EB] rounded-2xl shadow-xl z-50 p-2 space-y-1 animate-in fade-in zoom-in-95 duration-100 text-xs"
                onMouseLeave={() => setProfileMenuOpen(false)}
              >
                <div className="p-2 border-b border-[#F3F4F6]">
                  <span className="font-bold text-[#1A1A1A] block">{state.user.displayName || 'Alex'}</span>
                  <span className="text-[11px] text-[#6B7280] block truncate">{state.user.primaryEmail}</span>
                </div>
                <button
                  onClick={() => {
                    setActiveTab('account');
                    setProfileMenuOpen(false);
                  }}
                  className="w-full text-left p-2 rounded-lg hover:bg-[#F9FAFB] text-[#4B5563] font-medium flex items-center gap-2"
                >
                  <User className="w-3.5 h-3.5" />
                  <span>Account & Passkeys</span>
                </button>
                <button
                  onClick={() => {
                    setActiveTab('settings');
                    setProfileMenuOpen(false);
                  }}
                  className="w-full text-left p-2 rounded-lg hover:bg-[#F9FAFB] text-[#4B5563] font-medium flex items-center gap-2"
                >
                  <Settings className="w-3.5 h-3.5" />
                  <span>Privacy & Settings</span>
                </button>
                <div className="h-[1px] bg-[#F3F4F6] my-1" />
                <button
                  onClick={() => {
                    logout();
                    setProfileMenuOpen(false);
                    showToast('Logged out of workspace.');
                  }}
                  className="w-full text-left p-2 rounded-lg hover:bg-rose-50 text-rose-600 font-medium flex items-center gap-2"
                >
                  <LogOut className="w-3.5 h-3.5" />
                  <span>Sign out</span>
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Main Content Body */}
        <div className="w-full max-w-6xl mx-auto px-4 sm:px-8 py-6 pb-24 md:pb-8 space-y-8">
          
          {/* =============================================================== */}
          {/* TAB 1: OVERVIEW (MATCHING USER MOCKUP EXACTLY)                   */}
          {/* =============================================================== */}
          {activeTab === 'overview' && (
            <div className="space-y-8 animate-in fade-in duration-200">
              
              {/* Header Greeting */}
              <div className="space-y-1">
                <h1 className="text-3xl sm:text-4xl font-extrabold text-[#1A1A1A] tracking-tight">
                  {greeting}, {firstName}.
                </h1>
                <p className="text-sm sm:text-base text-[#6B7280]">
                  Continue where you left off or calculate something new.
                </p>
              </div>

              {/* Large Search Bar */}
              <div className="relative">
                <div className="relative rounded-2xl border border-[#E5E7EB] bg-white hover:border-[#D1D5DB] focus-within:border-[#0066FF] focus-within:ring-2 focus-within:ring-blue-100 transition-all shadow-xs">
                  <Search className="w-5 h-5 text-[#9CA3AF] absolute left-4 top-1/2 -translate-y-1/2" />
                  <input
                    ref={searchInputRef}
                    type="text"
                    placeholder="What do you want to calculate?"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onFocus={() => setSearchFocused(true)}
                    onBlur={() => setTimeout(() => setSearchFocused(false), 200)}
                    className="w-full pl-12 pr-4 py-4 text-sm sm:text-base font-semibold text-[#1A1A1A] placeholder-[#9CA3AF] outline-none bg-transparent rounded-2xl"
                  />
                  {searchQuery && (
                    <button
                      onClick={() => setSearchQuery('')}
                      className="absolute right-4 top-1/2 -translate-y-1/2 p-1 text-[#9CA3AF] hover:text-[#1A1A1A]"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>

                {/* Autocomplete / Quick Search Results */}
                {searchFocused && searchQuery.trim() && (
                  <div className="absolute left-0 right-0 mt-2 bg-white border border-[#E5E7EB] rounded-2xl shadow-xl z-50 p-2 space-y-1 animate-in fade-in duration-100">
                    <div className="px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-[#9CA3AF]">
                      Matching Calculators
                    </div>
                    {searchResults.length > 0 ? (
                      searchResults.map((tool) => (
                        <button
                          key={tool.id}
                          type="button"
                          onMouseDown={() => handleLaunchTool(tool.id)}
                          className="w-full p-3 rounded-xl hover:bg-[#F9FAFB] text-left flex items-center justify-between gap-3 group transition-colors"
                        >
                          <div>
                            <div className="text-xs font-bold text-[#1A1A1A] group-hover:text-[#0066FF]">
                              {tool.name}
                            </div>
                            <div className="text-[11px] text-[#6B7280]">{tool.subtitle}</div>
                          </div>
                          <ChevronRight className="w-4 h-4 text-[#9CA3AF] group-hover:text-[#0066FF]" />
                        </button>
                      ))
                    ) : (
                      <p className="text-xs text-[#6B7280] p-3 text-center">
                        No calculator found for "{searchQuery}". Try "mortgage", "currency", or "salary".
                      </p>
                    )}
                  </div>
                )}
              </div>

              {/* ------------------------------------------------------------- */}
              {/* SECTION: CONTINUE WHERE YOU LEFT OFF (Max 3 real elements)    */}
              {/* ------------------------------------------------------------- */}
              <div className="space-y-3">
                <h2 className="text-xs font-bold text-[#6B7280] uppercase tracking-wider">
                  Continue where you left off
                </h2>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  {/* Card 1: Currency Conversion (USD to EUR) */}
                  <button
                    onClick={() => handleLaunchTool('currency-converter')}
                    className="p-6 bg-white border border-[#E5E7EB] rounded-2xl hover:border-[#D1D5DB] transition-all text-left shadow-xs group cursor-pointer"
                  >
                    <div className="flex items-center justify-between text-xs font-semibold text-[#6B7280] mb-3">
                      <span>USD to EUR</span>
                      <div className="p-1.5 rounded-lg bg-[#F3F4F6] text-[#4B5563] group-hover:bg-[#E6F4F1] group-hover:text-[#0F766E] transition-colors">
                        <RefreshCw className="w-4 h-4" />
                      </div>
                    </div>
                    <div className="text-2xl sm:text-3xl font-bold text-[#1A1A1A] tracking-tight">
                      {recentFxRate}
                    </div>
                  </button>

                  {/* Card 2: US Salary */}
                  <button
                    onClick={() => handleLaunchTool('salary-engine')}
                    className="p-6 bg-white border border-[#E5E7EB] rounded-2xl hover:border-[#D1D5DB] transition-all text-left shadow-xs group cursor-pointer"
                  >
                    <div className="flex items-center justify-between text-xs font-semibold text-[#6B7280] mb-3">
                      <span>US Salary</span>
                      <div className="p-1.5 rounded-lg bg-[#F3F4F6] text-[#4B5563] group-hover:bg-[#E6F4F1] group-hover:text-[#0F766E] transition-colors">
                        <Banknote className="w-4 h-4" />
                      </div>
                    </div>
                    <div className="text-2xl sm:text-3xl font-bold text-[#1A1A1A] tracking-tight">
                      $120k
                    </div>
                  </button>

                  {/* Card 3: Mortgage Scenario */}
                  <button
                    onClick={() => handleLaunchTool('loan-engine')}
                    className="p-6 bg-white border border-[#E5E7EB] rounded-2xl hover:border-[#D1D5DB] transition-all text-left shadow-xs group cursor-pointer"
                  >
                    <div className="flex items-center justify-between text-xs font-semibold text-[#6B7280] mb-3">
                      <span>Mortgage Scenario</span>
                      <div className="p-1.5 rounded-lg bg-[#F3F4F6] text-[#4B5563] group-hover:bg-[#E6F4F1] group-hover:text-[#0F766E] transition-colors">
                        <Home className="w-4 h-4" />
                      </div>
                    </div>
                    <div className="text-2xl sm:text-3xl font-bold text-[#1A1A1A] tracking-tight">
                      6.5%
                    </div>
                  </button>
                </div>
              </div>

              {/* ------------------------------------------------------------- */}
              {/* SECTION: QUICK TOOLS (6 clean shortcuts)                      */}
              {/* ------------------------------------------------------------- */}
              <div className="space-y-3">
                <h2 className="text-xs font-bold text-[#6B7280] uppercase tracking-wider">
                  Quick Tools
                </h2>

                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-3 sm:gap-4">
                  {[
                    { id: 'currency-converter', label: 'Currency', icon: RefreshCw },
                    { id: 'salary-engine', label: 'Salary', icon: Banknote },
                    { id: 'loan-engine', label: 'Loans', icon: Home },
                    { id: 'compound-interest-calculator', label: 'Compound Interest', icon: TrendingUp },
                    { id: 'inflation-purchasing-power', label: 'Inflation', icon: TrendingDown },
                    { id: 'vat-sales-tax', label: 'Tax', icon: Receipt },
                  ].map((tool) => {
                    const Icon = tool.icon;
                    return (
                      <button
                        key={tool.id}
                        onClick={() => handleLaunchTool(tool.id)}
                        className="p-5 bg-white border border-[#E5E7EB] rounded-2xl hover:border-[#D1D5DB] hover:bg-[#F9FAFB] transition-all flex flex-col items-center justify-center text-center gap-3 shadow-xs group cursor-pointer min-h-[110px]"
                      >
                        <div className="p-2 rounded-xl bg-[#F3F4F6] text-[#4B5563] group-hover:bg-[#E6F4F1] group-hover:text-[#0F766E] transition-colors">
                          <Icon className="w-5 h-5" />
                        </div>
                        <span className="text-xs font-bold text-[#1A1A1A] group-hover:text-[#0066FF] transition-colors">
                          {tool.label}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* ------------------------------------------------------------- */}
              {/* SECTION: RECENT SAVED & ACTIVE ALERTS (Split Row)              */}
              {/* ------------------------------------------------------------- */}
              <div className="grid lg:grid-cols-12 gap-6 items-start">
                
                {/* RECENT SAVED (~65% width) */}
                <div className="lg:col-span-8 space-y-3">
                  <div className="flex items-center justify-between">
                    <h2 className="text-xs font-bold text-[#6B7280] uppercase tracking-wider">
                      Recent Saved
                    </h2>
                    {state.savedCalculations.length > 0 && (
                      <button
                        onClick={() => setActiveTab('saved')}
                        className="text-xs font-bold text-[#0F766E] hover:underline cursor-pointer"
                      >
                        View all ({state.savedCalculations.length})
                      </button>
                    )}
                  </div>

                  <div className="bg-white border border-[#E5E7EB] rounded-2xl shadow-xs overflow-hidden">
                    {state.savedCalculations.length === 0 ? (
                      <div className="p-8 text-center space-y-3">
                        <div className="w-10 h-10 rounded-full bg-[#F3F4F6] text-[#9CA3AF] flex items-center justify-center mx-auto">
                          <Bookmark className="w-5 h-5" />
                        </div>
                        <div className="space-y-1">
                          <h3 className="text-sm font-bold text-[#1A1A1A]">No saved calculations yet</h3>
                          <p className="text-xs text-[#6B7280] max-w-sm mx-auto">
                            Run any calculation on YamooPay and click "Save" to keep your scenarios accessible here.
                          </p>
                        </div>
                        <button
                          onClick={() => setActiveTab('tools')}
                          className="px-4 py-2 bg-[#F3F4F6] hover:bg-[#E5E7EB] text-[#1A1A1A] font-bold text-xs rounded-xl transition-colors inline-flex items-center gap-1.5"
                        >
                          <span>Explore Tools</span>
                          <ArrowRight className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    ) : (
                      <div className="divide-y divide-[#F3F4F6]">
                        {state.savedCalculations.slice(0, 3).map((calc) => {
                          // Pick icon based on calculation tool
                          const isSalary = calc.toolId.includes('salary');
                          const isLoan = calc.toolId.includes('loan') || calc.toolId.includes('mortgage');
                          const Icon = isSalary ? Banknote : isLoan ? Home : RefreshCw;

                          return (
                            <div
                              key={calc.id}
                              onClick={() => handleLaunchTool(calc.toolId)}
                              className="p-4 sm:p-5 hover:bg-[#F9FAFB] transition-colors flex items-center justify-between gap-4 cursor-pointer group"
                            >
                              <div className="flex items-center gap-3.5 min-w-0">
                                <div className="w-10 h-10 rounded-xl bg-[#F3F4F6] group-hover:bg-[#E6F4F1] text-[#4B5563] group-hover:text-[#0F766E] flex items-center justify-center shrink-0 transition-colors">
                                  <Icon className="w-5 h-5" />
                                </div>
                                <div className="min-w-0">
                                  <span className="text-xs sm:text-sm font-bold text-[#1A1A1A] block truncate group-hover:text-[#0066FF] transition-colors">
                                    {calc.title}
                                  </span>
                                  <span className="text-[11px] text-[#6B7280] block mt-0.5">
                                    Updated {getRelativeTime(calc.timestamp)}
                                  </span>
                                </div>
                              </div>

                              <div className="text-right shrink-0">
                                <span className="text-xs sm:text-sm font-extrabold text-[#1A1A1A] block">
                                  {calc.primaryResult}
                                </span>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>

                {/* ACTIVE ALERTS (~35% width) */}
                <div className="lg:col-span-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <h2 className="text-xs font-bold text-[#6B7280] uppercase tracking-wider">
                      Active Alerts
                    </h2>
                    {state.alerts.length > 0 && (
                      <button
                        onClick={() => setActiveTab('alerts')}
                        className="text-xs font-bold text-[#0F766E] hover:underline cursor-pointer"
                      >
                        Manage
                      </button>
                    )}
                  </div>

                  <div className="bg-white border border-[#E5E7EB] rounded-2xl p-5 shadow-xs space-y-4">
                    {state.alerts.length === 0 ? (
                      <div className="text-center py-4 space-y-3">
                        <div className="w-10 h-10 rounded-full bg-[#FEF3C7] text-[#B45309] flex items-center justify-center mx-auto">
                          <Bell className="w-5 h-5" />
                        </div>
                        <div className="space-y-1">
                          <h3 className="text-xs font-bold text-[#1A1A1A]">No active alerts</h3>
                          <p className="text-[11px] text-[#6B7280]">
                            Configure exchange rate alerts to receive threshold notifications.
                          </p>
                        </div>
                        <button
                          onClick={() => {
                            setActiveTab('alerts');
                            setShowAddAlert(true);
                          }}
                          className="px-3.5 py-1.5 bg-[#F3F4F6] hover:bg-[#E5E7EB] text-[#1A1A1A] font-bold text-xs rounded-xl transition-colors"
                        >
                          Create Rate Alert
                        </button>
                      </div>
                    ) : (
                      <>
                        <div className="flex items-start gap-3">
                          <div className="p-2.5 rounded-xl bg-[#FFF1EE] text-[#EA580C] shrink-0">
                            <Bell className="w-5 h-5" />
                          </div>
                          <div className="space-y-1">
                            <span className="text-sm font-bold text-[#1A1A1A] block">
                              {state.alerts.length} active FX {state.alerts.length === 1 ? 'alert' : 'alerts'}
                            </span>
                            <p className="text-xs text-[#6B7280] leading-relaxed">
                              {state.alerts[0]?.targetAsset || 'EUR/USD'} is nearing your target of {state.alerts[0]?.targetRate || '1.10'}. Review your saved scenarios.
                            </p>
                          </div>
                        </div>

                        <button
                          onClick={() => setActiveTab('alerts')}
                          className="text-xs font-bold text-[#0066FF] hover:underline flex items-center gap-1 cursor-pointer pt-1"
                        >
                          <span>Review Alerts</span>
                          <ArrowRight className="w-3.5 h-3.5" />
                        </button>
                      </>
                    )}
                  </div>
                </div>

              </div>

            </div>
          )}

          {/* =============================================================== */}
          {/* TAB 2: TOOLS LIBRARY (AUTHENTICATED TOOLS WORKSPACE)             */}
          {/* =============================================================== */}
          {activeTab === 'tools' && (
            <div className="space-y-8 animate-in fade-in duration-200">
              
              {/* Header: Title, Subtitle, and Search bar on the right */}
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div className="space-y-1">
                  <h1 className="text-3xl sm:text-4xl font-extrabold text-[#1A1A1A] tracking-tight">
                    Tools Library
                  </h1>
                  <p className="text-sm sm:text-base text-[#6B7280]">
                    Your financial workspace. Discover calculators, estimators, and planning tools.
                  </p>
                </div>

                {/* Search input desktop/tablet right */}
                <div className="relative w-full md:w-80 shrink-0">
                  <Search className="w-4 h-4 text-[#9CA3AF] absolute left-3.5 top-1/2 -translate-y-1/2" />
                  <input
                    ref={toolsSearchInputRef}
                    type="text"
                    placeholder="Search tools..."
                    value={toolsSearchQuery}
                    onChange={(e) => {
                      setToolsSearchQuery(e.target.value);
                      setToolsVisibleCount(6);
                    }}
                    className="w-full pl-10 pr-9 py-2.5 bg-white border border-[#E5E7EB] hover:border-[#D1D5DB] focus:border-[#0F766E] focus:ring-2 focus:ring-teal-100/50 rounded-xl text-sm font-medium text-[#1A1A1A] placeholder-[#9CA3AF] outline-none transition-all shadow-2xs"
                  />
                  {toolsSearchQuery && (
                    <button
                      onClick={() => setToolsSearchQuery('')}
                      className="absolute right-3 top-1/2 -translate-y-1/2 p-0.5 text-[#9CA3AF] hover:text-[#1A1A1A]"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              </div>

              {/* Category Filter Tabs */}
              <div className="flex items-center justify-between border-b border-[#E5E7EB] gap-4 overflow-x-auto no-scrollbar">
                <div className="flex items-center gap-8">
                  {TOOL_CATEGORIES.map((cat) => {
                    const isActive = toolsCategory === cat;
                    return (
                      <button
                        key={cat}
                        onClick={() => {
                          setToolsCategory(cat);
                          setToolsVisibleCount(6);
                        }}
                        className={`pb-3.5 text-sm font-semibold transition-all relative flex items-center gap-2 whitespace-nowrap cursor-pointer ${
                          isActive
                            ? 'text-[#1A1A1A] font-bold'
                            : 'text-[#6B7280] hover:text-[#1A1A1A]'
                        }`}
                      >
                        <span>{cat}</span>
                        {cat === 'Everyday Money' && (
                          <span className="px-1.5 py-0.5 text-[9px] font-bold bg-[#064E3B] text-white rounded">
                            NEW
                          </span>
                        )}
                        {isActive && (
                          <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#1A1A1A]" />
                        )}
                      </button>
                    );
                  })}
                </div>

                {/* Auth-only enhancement: Filter by favorites toggle */}
                <div className="pb-3 hidden sm:flex items-center gap-2">
                  <button
                    onClick={() => {
                      setToolsFilterFavorites(!toolsFilterFavorites);
                      setToolsVisibleCount(6);
                    }}
                    className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-colors flex items-center gap-1.5 cursor-pointer ${
                      toolsFilterFavorites
                        ? 'bg-[#E6F4F1] text-[#0F766E]'
                        : 'text-[#6B7280] hover:text-[#1A1A1A] hover:bg-[#F3F4F6]'
                    }`}
                  >
                    <Bookmark className={`w-3.5 h-3.5 ${toolsFilterFavorites ? 'fill-[#0F766E] text-[#0F766E]' : ''}`} />
                    <span>Favorites ({state.favorites.filter((f) => f.type === 'TOOL').length})</span>
                  </button>
                </div>
              </div>

              {/* Tools Cards Grid */}
              {visibleTools.length === 0 ? (
                <div className="p-12 text-center bg-white border border-[#E5E7EB] rounded-2xl space-y-4">
                  <div className="w-12 h-12 rounded-2xl bg-[#F3F4F6] text-[#9CA3AF] flex items-center justify-center mx-auto">
                    <Search className="w-6 h-6" />
                  </div>
                  <div className="space-y-1">
                    <h3 className="text-base font-bold text-[#1A1A1A]">No tools found</h3>
                    <p className="text-xs text-[#6B7280] max-w-sm mx-auto">
                      {toolsFilterFavorites
                        ? "You haven't saved any favorite tools yet. Click the bookmark icon on any tool card to add it here."
                        : 'No calculators match your current filter or search criteria.'}
                    </p>
                  </div>
                  <button
                    onClick={() => {
                      setToolsCategory('All');
                      setToolsSearchQuery('');
                      setToolsFilterFavorites(false);
                      setToolsVisibleCount(6);
                    }}
                    className="px-4 py-2 bg-[#F3F4F6] hover:bg-[#E5E7EB] text-[#1A1A1A] font-bold text-xs rounded-xl transition-colors cursor-pointer"
                  >
                    Reset all filters
                  </button>
                </div>
              ) : (
                <div className="space-y-8">
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                    {visibleTools.map((tool) => {
                      const meta = getToolDisplayMeta(tool);
                      const IconComponent = meta.icon;
                      const isFav = isToolFavorite(tool.id);
                      const savedCount = state.savedCalculations.filter(
                        (c) => c.toolId === tool.id || c.toolId === tool.slug
                      ).length;

                      return (
                        <div
                          key={tool.id}
                          onClick={() => handleLaunchTool(tool.id)}
                          className="bg-white border border-[#E5E7EB] hover:border-[#CBD5E1] rounded-2xl p-6 transition-all flex flex-col justify-between cursor-pointer group shadow-2xs hover:shadow-sm"
                        >
                          <div>
                            {/* Card Top Row: Icon & Bookmark */}
                            <div className="flex items-center justify-between">
                              <div className="w-10 h-10 rounded-xl bg-[#F3F4F6] group-hover:bg-[#E6F4F1] text-[#374151] group-hover:text-[#0F766E] flex items-center justify-center transition-colors">
                                <IconComponent className="w-5 h-5" />
                              </div>

                              <button
                                onClick={(e) => handleToggleToolFavorite(e, tool)}
                                className={`p-2 rounded-lg transition-colors cursor-pointer ${
                                  isFav
                                    ? 'text-[#0F766E] hover:bg-teal-50'
                                    : 'text-[#9CA3AF] hover:text-[#1A1A1A] hover:bg-[#F3F4F6]'
                                }`}
                                title={isFav ? 'Remove from favorites' : 'Save to favorites'}
                              >
                                <Bookmark className={`w-4 h-4 ${isFav ? 'fill-[#0F766E] text-[#0F766E]' : ''}`} />
                              </button>
                            </div>

                            {/* Title & Description */}
                            <h3 className="text-lg font-bold text-[#1A1A1A] group-hover:text-[#0066FF] transition-colors mt-5 mb-2">
                              {meta.title}
                            </h3>
                            <p className="text-xs text-[#6B7280] leading-relaxed line-clamp-2">
                              {meta.description}
                            </p>
                          </div>

                          {/* Auth-only badges / metadata */}
                          <div className="mt-5 pt-3.5 border-t border-[#F9FAFB] flex items-center justify-between text-[11px]">
                            {savedCount > 0 ? (
                              <span className="inline-flex items-center gap-1 font-semibold text-[#0F766E]">
                                <CheckCircle2 className="w-3 h-3" />
                                <span>
                                  {savedCount} saved {savedCount === 1 ? 'scenario' : 'scenarios'}
                                </span>
                              </span>
                            ) : (
                              <span className="text-[#9CA3AF] font-medium">
                                {tool.category}
                              </span>
                            )}

                            <span className="text-[#9CA3AF] text-[10px] font-bold uppercase tracking-wider">
                              {tool.regionsSupported?.includes('Global')
                                ? 'Global'
                                : tool.marketSupport?.[0] || 'Standard'}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Load More Tools Button */}
                  {authenticatedFilteredTools.length > toolsVisibleCount && (
                    <div className="flex flex-col items-center justify-center pt-2 space-y-2">
                      <button
                        onClick={() => setToolsVisibleCount((prev) => prev + 6)}
                        className="px-6 py-2.5 bg-[#F3F4F6] hover:bg-[#E5E7EB] text-[#1A1A1A] font-semibold text-xs rounded-xl transition-colors cursor-pointer shadow-2xs"
                      >
                        Load More Tools
                      </button>
                      <span className="text-[11px] text-[#9CA3AF]">
                        Showing {visibleTools.length} of {authenticatedFilteredTools.length} tools
                      </span>
                    </div>
                  )}
                </div>
              )}

            </div>
          )}

          {/* =============================================================== */}
          {/* TAB 3: SAVED (YOUR SAVED CALCULATIONS AND FINANCIAL SCENARIOS)  */}
          {/* =============================================================== */}
          {activeTab === 'saved' && (
            <div className="space-y-6 animate-in fade-in duration-200">
              {/* Header */}
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                  <h1 className="text-2xl font-black text-[#1A1A1A] tracking-tight">
                    Saved
                  </h1>
                  <p className="text-xs text-[#6B7280] mt-0.5">
                    Your saved calculations and financial scenarios.
                  </p>
                </div>
                {state.savedCalculations.length > 0 && (
                  <button
                    onClick={() => {
                      exportUserDataJSON();
                      showToast('Workspace data exported as JSON file.');
                    }}
                    className="px-3.5 py-2 bg-white border border-[#E5E7EB] text-[#4B5563] font-bold text-xs rounded-xl hover:bg-[#F9FAFB] flex items-center gap-1.5 self-start sm:self-auto cursor-pointer shadow-2xs"
                  >
                    <Download className="w-3.5 h-3.5" />
                    <span>Export JSON</span>
                  </button>
                )}
              </div>

              {/* Filters */}
              <div className="flex items-center gap-2 overflow-x-auto pb-1 scrollbar-none">
                {[
                  { id: 'All', label: 'All' },
                  { id: 'Currency', label: 'Currency' },
                  { id: 'Income & Tax', label: 'Income & Tax' },
                  { id: 'Borrow', label: 'Borrow' },
                  { id: 'Save & Invest', label: 'Save & Invest' },
                  { id: 'Everyday', label: 'Everyday' },
                ].map((filter) => (
                  <button
                    key={filter.id}
                    onClick={() => setSavedCategoryFilter(filter.id as any)}
                    className={`min-h-[38px] px-3.5 py-1.5 rounded-full text-xs font-bold transition-all shrink-0 cursor-pointer ${
                      savedCategoryFilter === filter.id
                        ? 'bg-[#1A1A1A] text-white shadow-2xs'
                        : 'bg-white border border-[#E5E7EB] text-[#4B5563] hover:bg-[#F9FAFB]'
                    }`}
                  >
                    {filter.label}
                  </button>
                ))}
              </div>

              {/* Saved Content or Empty State */}
              {filteredSavedCalculations.length === 0 ? (
                /* Empty State */
                <div className="p-12 text-center bg-white border border-[#E5E7EB] rounded-2xl space-y-4 my-4 shadow-2xs">
                  <div className="w-12 h-12 rounded-full bg-[#E6F4F1] text-[#0F766E] flex items-center justify-center mx-auto">
                    <Bookmark className="w-6 h-6" />
                  </div>
                  <div className="space-y-1">
                    <h3 className="text-base font-bold text-[#1A1A1A]">No saved calculations yet.</h3>
                    <p className="text-xs text-[#6B7280] max-w-sm mx-auto">
                      Use any YamooPay calculator and save your custom scenarios to compare and review them here.
                    </p>
                  </div>
                  <button
                    onClick={() => setActiveTab('tools')}
                    className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#0F766E] text-white font-bold text-xs rounded-xl hover:bg-[#0D635C] transition-all cursor-pointer shadow-2xs"
                  >
                    <span>Explore tools</span>
                    <ArrowRight className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Desktop Table View (md & lg) */}
                  <div className="hidden md:block bg-white border border-[#E5E7EB] rounded-2xl shadow-2xs overflow-hidden">
                    <div className="overflow-x-auto">
                      <table className="w-full text-left border-collapse">
                        <thead>
                          <tr className="border-b border-[#E5E7EB] bg-[#F9FAFB] text-[11px] font-bold text-[#6B7280] uppercase tracking-wider">
                            <th className="py-3 px-4 font-bold">Title</th>
                            <th className="py-3 px-4 font-bold">Tool</th>
                            <th className="py-3 px-4 font-bold">Market</th>
                            <th className="py-3 px-4 font-bold">Key result</th>
                            <th className="py-3 px-4 font-bold">Updated</th>
                            <th className="py-3 px-4 font-bold text-right">Actions</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-[#F3F4F6] text-xs">
                          {filteredSavedCalculations.map((calc) => (
                            <tr
                              key={calc.id}
                              className={`hover:bg-[#F9FAFB] transition-colors ${
                                calc.isArchived ? 'opacity-60 bg-gray-50/50' : ''
                              }`}
                            >
                              {/* Title */}
                              <td className="py-3.5 px-4">
                                <div className="space-y-0.5">
                                  <div className="flex items-center gap-2">
                                    <span className="font-bold text-[#1A1A1A] block">
                                      {calc.title}
                                    </span>
                                    {calc.isArchived && (
                                      <span className="px-1.5 py-0.5 bg-gray-200 text-gray-700 text-[10px] font-semibold rounded">
                                        Archived
                                      </span>
                                    )}
                                  </div>
                                  {calc.inputsSummary && (
                                    <span className="text-[11px] text-[#6B7280] block truncate max-w-xs">
                                      {calc.inputsSummary}
                                    </span>
                                  )}
                                </div>
                              </td>

                              {/* Tool */}
                              <td className="py-3.5 px-4 font-medium text-[#374151] whitespace-nowrap">
                                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-[#F3F4F6] text-[#4B5563] rounded-lg text-[11px] font-semibold">
                                  {calc.toolName || 'Calculator'}
                                </span>
                              </td>

                              {/* Market */}
                              <td className="py-3.5 px-4 whitespace-nowrap">
                                <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-teal-50 text-[#0F766E] border border-teal-100 rounded-lg text-[11px] font-bold">
                                  {calc.market || calc.region || 'Global'}
                                </span>
                              </td>

                              {/* Key result */}
                              <td className="py-3.5 px-4 whitespace-nowrap">
                                <span className="font-bold text-[#1A1A1A] text-xs sm:text-sm">
                                  {calc.primaryResult}
                                </span>
                              </td>

                              {/* Updated */}
                              <td className="py-3.5 px-4 whitespace-nowrap text-[#6B7280] text-[11px]">
                                {calc.createdAt
                                  ? new Date(calc.createdAt).toLocaleDateString(undefined, {
                                      month: 'short',
                                      day: 'numeric',
                                      year: 'numeric',
                                    })
                                  : 'Recently'}
                              </td>

                              {/* Actions */}
                              <td className="py-3.5 px-4 whitespace-nowrap text-right">
                                <div className="flex items-center justify-end gap-2 relative">
                                  {/* Primary Action: Open */}
                                  <button
                                    onClick={() => handleLaunchTool(calc.toolId)}
                                    className="px-3 py-1.5 bg-[#0F766E] hover:bg-[#0D635C] text-white font-bold text-xs rounded-lg transition-all cursor-pointer shadow-2xs inline-flex items-center gap-1"
                                  >
                                    <span>Open</span>
                                  </button>

                                  {/* Secondary Action: Compare */}
                                  {calc.canCompare !== false && (
                                    <button
                                      onClick={() => {
                                        setComparingCalc(calc);
                                      }}
                                      className="px-2.5 py-1.5 bg-white border border-[#E5E7EB] hover:bg-[#F9FAFB] text-[#374151] font-bold text-xs rounded-lg transition-all cursor-pointer inline-flex items-center gap-1"
                                    >
                                      <Scale className="w-3.5 h-3.5 text-[#0F766E]" />
                                      <span>Compare</span>
                                    </button>
                                  )}

                                  {/* Kebab Button */}
                                  <div className="relative">
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setActiveKebabId(
                                          activeKebabId === calc.id ? null : calc.id
                                        );
                                      }}
                                      className="p-1.5 text-[#6B7280] hover:text-[#1A1A1A] rounded-lg hover:bg-[#F3F4F6] transition-colors cursor-pointer"
                                      title="More actions"
                                    >
                                      <MoreVertical className="w-4 h-4" />
                                    </button>

                                    {/* Kebab Dropdown Menu */}
                                    {activeKebabId === calc.id && (
                                      <div className="absolute right-0 mt-1 w-44 bg-white border border-[#E5E7EB] rounded-xl shadow-lg z-30 py-1 text-left animate-in fade-in duration-100">
                                        <button
                                          onClick={() => {
                                            setActiveKebabId(null);
                                            setRenamingCalc(calc);
                                            setRenamingTitle(calc.title);
                                          }}
                                          className="w-full px-3.5 py-2 text-xs font-semibold text-[#374151] hover:bg-[#F9FAFB] flex items-center gap-2 cursor-pointer"
                                        >
                                          <Edit3 className="w-3.5 h-3.5 text-[#6B7280]" />
                                          <span>Rename</span>
                                        </button>
                                        <button
                                          onClick={() => {
                                            setActiveKebabId(null);
                                            duplicateSavedCalculation(calc.id);
                                            showToast('Calculation duplicated.');
                                          }}
                                          className="w-full px-3.5 py-2 text-xs font-semibold text-[#374151] hover:bg-[#F9FAFB] flex items-center gap-2 cursor-pointer"
                                        >
                                          <Copy className="w-3.5 h-3.5 text-[#6B7280]" />
                                          <span>Duplicate</span>
                                        </button>
                                        <button
                                          onClick={() => {
                                            setActiveKebabId(null);
                                            archiveSavedCalculation(calc.id);
                                            showToast(
                                              calc.isArchived
                                                ? 'Calculation restored.'
                                                : 'Calculation archived.'
                                            );
                                          }}
                                          className="w-full px-3.5 py-2 text-xs font-semibold text-[#374151] hover:bg-[#F9FAFB] flex items-center gap-2 cursor-pointer"
                                        >
                                          <Archive className="w-3.5 h-3.5 text-[#6B7280]" />
                                          <span>{calc.isArchived ? 'Unarchive' : 'Archive'}</span>
                                        </button>
                                        <div className="my-1 border-t border-[#F3F4F6]" />
                                        <button
                                          onClick={() => {
                                            setActiveKebabId(null);
                                            deleteSavedCalculation(calc.id);
                                            showToast('Calculation deleted.');
                                          }}
                                          className="w-full px-3.5 py-2 text-xs font-semibold text-rose-600 hover:bg-rose-50 flex items-center gap-2 cursor-pointer"
                                        >
                                          <Trash2 className="w-3.5 h-3.5 text-rose-600" />
                                          <span>Delete</span>
                                        </button>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Mobile Cards View (< md) */}
                  <div className="md:hidden space-y-3">
                    {filteredSavedCalculations.map((calc) => (
                      <div
                        key={calc.id}
                        className={`p-4 bg-white border border-[#E5E7EB] rounded-2xl shadow-2xs space-y-3 ${
                          calc.isArchived ? 'opacity-60 bg-gray-50/50' : ''
                        }`}
                      >
                        {/* Title & Kebab */}
                        <div className="flex items-start justify-between gap-2">
                          <div>
                            <span className="text-sm font-bold text-[#1A1A1A] block leading-snug">
                              {calc.title}
                            </span>
                            <div className="flex items-center gap-2 mt-1">
                              <span className="px-2 py-0.5 bg-[#F3F4F6] text-[#4B5563] font-semibold text-[10px] rounded">
                                {calc.toolName || 'Calculator'}
                              </span>
                              <span className="px-2 py-0.5 bg-teal-50 text-[#0F766E] font-bold text-[10px] rounded border border-teal-100">
                                {calc.market || calc.region || 'Global'}
                              </span>
                            </div>
                          </div>

                          {/* Kebab Mobile */}
                          <div className="relative shrink-0">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setActiveKebabId(activeKebabId === calc.id ? null : calc.id);
                              }}
                              className="p-1.5 text-[#6B7280] hover:text-[#1A1A1A] rounded-lg hover:bg-[#F3F4F6] transition-colors cursor-pointer"
                            >
                              <MoreVertical className="w-4 h-4" />
                            </button>

                            {activeKebabId === calc.id && (
                              <div className="absolute right-0 mt-1 w-44 bg-white border border-[#E5E7EB] rounded-xl shadow-lg z-30 py-1 text-left">
                                <button
                                  onClick={() => {
                                    setActiveKebabId(null);
                                    setRenamingCalc(calc);
                                    setRenamingTitle(calc.title);
                                  }}
                                  className="w-full px-3.5 py-2 text-xs font-semibold text-[#374151] hover:bg-[#F9FAFB] flex items-center gap-2"
                                >
                                  <Edit3 className="w-3.5 h-3.5 text-[#6B7280]" />
                                  <span>Rename</span>
                                </button>
                                <button
                                  onClick={() => {
                                    setActiveKebabId(null);
                                    duplicateSavedCalculation(calc.id);
                                    showToast('Calculation duplicated.');
                                  }}
                                  className="w-full px-3.5 py-2 text-xs font-semibold text-[#374151] hover:bg-[#F9FAFB] flex items-center gap-2"
                                >
                                  <Copy className="w-3.5 h-3.5 text-[#6B7280]" />
                                  <span>Duplicate</span>
                                </button>
                                <button
                                  onClick={() => {
                                    setActiveKebabId(null);
                                    archiveSavedCalculation(calc.id);
                                    showToast(
                                      calc.isArchived
                                        ? 'Calculation restored.'
                                        : 'Calculation archived.'
                                    );
                                  }}
                                  className="w-full px-3.5 py-2 text-xs font-semibold text-[#374151] hover:bg-[#F9FAFB] flex items-center gap-2"
                                >
                                  <Archive className="w-3.5 h-3.5 text-[#6B7280]" />
                                  <span>{calc.isArchived ? 'Unarchive' : 'Archive'}</span>
                                </button>
                                <div className="my-1 border-t border-[#F3F4F6]" />
                                <button
                                  onClick={() => {
                                    setActiveKebabId(null);
                                    deleteSavedCalculation(calc.id);
                                    showToast('Calculation deleted.');
                                  }}
                                  className="w-full px-3.5 py-2 text-xs font-semibold text-rose-600 hover:bg-rose-50 flex items-center gap-2"
                                >
                                  <Trash2 className="w-3.5 h-3.5 text-rose-600" />
                                  <span>Delete</span>
                                </button>
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Result & Timestamp */}
                        <div className="p-3 bg-[#F9FAFB] rounded-xl flex items-center justify-between border border-[#F3F4F6]">
                          <span className="text-xs font-bold text-[#1A1A1A]">
                            {calc.primaryResult}
                          </span>
                          <span className="text-[10px] text-[#9CA3AF]">
                            {calc.createdAt
                              ? new Date(calc.createdAt).toLocaleDateString()
                              : 'Recently'}
                          </span>
                        </div>

                        {/* Mobile Action Buttons */}
                        <div className="flex items-center gap-2 pt-1">
                          <button
                            onClick={() => handleLaunchTool(calc.toolId)}
                            className="flex-1 py-2 bg-[#0F766E] text-white font-bold text-xs rounded-xl hover:bg-[#0D635C] transition-all text-center cursor-pointer shadow-2xs"
                          >
                            Open
                          </button>

                          {calc.canCompare !== false && (
                            <button
                              onClick={() => setComparingCalc(calc)}
                              className="px-3 py-2 bg-white border border-[#E5E7EB] text-[#374151] font-bold text-xs rounded-xl hover:bg-[#F9FAFB] transition-all cursor-pointer flex items-center justify-center gap-1"
                            >
                              <Scale className="w-3.5 h-3.5 text-[#0F766E]" />
                              <span>Compare</span>
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* =============================================================== */}
          {/* TAB 4: ALERTS MANAGEMENT                                        */}
          {/* =============================================================== */}
          {activeTab === 'alerts' && (
            <div className="space-y-6 animate-in fade-in duration-200">
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div>
                  <h1 className="text-2xl font-extrabold text-[#1A1A1A] tracking-tight">
                    Active Rate Alerts
                  </h1>
                  <p className="text-xs text-[#6B7280] mt-0.5">
                    Real-time threshold notifications for currency pairs and inflation indices.
                  </p>
                </div>
                <button
                  onClick={() => setShowAddAlert(true)}
                  className="px-4 py-2 bg-[#0066FF] hover:bg-[#0052CC] text-white text-xs font-bold rounded-xl transition-all shadow-xs flex items-center gap-1.5"
                >
                  <Plus className="w-4 h-4" />
                  <span>Create Alert</span>
                </button>
              </div>

              {/* Alert Creation Form Drawer */}
              {showAddAlert && (
                <div className="p-6 bg-white border border-[#0066FF] rounded-2xl shadow-md space-y-4 animate-in fade-in">
                  <div className="flex items-center justify-between">
                    <h3 className="text-sm font-bold text-[#1A1A1A]">New FX Rate Alert</h3>
                    <button
                      onClick={() => setShowAddAlert(false)}
                      className="p-1 text-[#9CA3AF] hover:text-[#1A1A1A]"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                  <form onSubmit={handleCreateAlert} className="grid sm:grid-cols-3 gap-4">
                    <div className="space-y-1">
                      <label className="text-[11px] font-bold text-[#4B5563]">Currency Pair</label>
                      <input
                        type="text"
                        value={alertPair}
                        onChange={(e) => setAlertPair(e.target.value.toUpperCase())}
                        placeholder="EUR/USD"
                        className="w-full p-2.5 bg-white border border-[#E5E7EB] rounded-xl text-xs font-bold text-[#1A1A1A] outline-none"
                      />
                    </div>
                    <div className="space-y-1">
                      <label className="text-[11px] font-bold text-[#4B5563]">Target Rate Threshold</label>
                      <input
                        type="text"
                        value={alertThreshold}
                        onChange={(e) => setAlertThreshold(e.target.value)}
                        placeholder="1.1000"
                        className="w-full p-2.5 bg-white border border-[#E5E7EB] rounded-xl text-xs font-bold text-[#1A1A1A] outline-none"
                      />
                    </div>
                    <div className="flex items-end">
                      <button
                        type="submit"
                        className="w-full py-2.5 px-4 bg-[#0066FF] text-white font-bold text-xs rounded-xl hover:bg-[#0052CC] transition-colors"
                      >
                        Save Alert
                      </button>
                    </div>
                  </form>
                </div>
              )}

              {state.alerts.length === 0 ? (
                <div className="p-12 text-center bg-white border border-[#E5E7EB] rounded-2xl space-y-3">
                  <div className="w-12 h-12 rounded-full bg-[#FEF3C7] text-[#B45309] flex items-center justify-center mx-auto">
                    <Bell className="w-6 h-6" />
                  </div>
                  <h3 className="text-base font-bold text-[#1A1A1A]">No active alerts</h3>
                  <p className="text-xs text-[#6B7280] max-w-md mx-auto">
                    Monitor market volatility and get notified when an exchange rate crosses your target threshold.
                  </p>
                  <button
                    onClick={() => setShowAddAlert(true)}
                    className="px-4 py-2 bg-[#0066FF] text-white font-bold text-xs rounded-xl hover:bg-[#0052CC]"
                  >
                    Create Rate Alert
                  </button>
                </div>
              ) : (
                <div className="space-y-3">
                  {state.alerts.map((al) => (
                    <div
                      key={al.id}
                      className="p-5 bg-white border border-[#E5E7EB] rounded-2xl hover:border-[#D1D5DB] transition-all flex items-center justify-between gap-4 shadow-xs"
                    >
                      <div className="flex items-center gap-3.5">
                        <div className="p-2.5 rounded-xl bg-[#FFF1EE] text-[#EA580C]">
                          <Bell className="w-5 h-5" />
                        </div>
                        <div>
                          <div className="text-sm font-bold text-[#1A1A1A]">
                            {al.targetAsset} cross target @ {al.targetRate}
                          </div>
                          <div className="text-xs text-[#6B7280]">
                            Channel: {al.channel} • Created {getRelativeTime(al.createdAt)}
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => toggleAlertStatus(al.id)}
                          className={`px-3 py-1 text-xs font-bold rounded-lg transition-colors ${
                            al.active
                              ? 'bg-[#E6F4F1] text-[#0F766E]'
                              : 'bg-[#F3F4F6] text-[#6B7280]'
                          }`}
                        >
                          {al.active ? 'Active' : 'Paused'}
                        </button>
                        <button
                          onClick={() => {
                            deleteGlobalAlert(al.id);
                            showToast('Alert removed');
                          }}
                          className="p-2 text-[#9CA3AF] hover:text-rose-600 rounded-xl hover:bg-rose-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* =============================================================== */}
          {/* TAB 5: ACTIVITY (RECENT ACTION LOG & HISTORICAL AUDIT)           */}
          {/* =============================================================== */}
          {activeTab === 'activity' && (
            <div className="space-y-6 animate-in fade-in duration-200">
              {/* Header */}
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                  <h1 className="text-2xl font-black text-[#1A1A1A] tracking-tight">
                    Recent Activity
                  </h1>
                  <p className="text-xs text-[#6B7280] mt-0.5">
                    Historical log of your calculations, FX triggers, and account security events.
                  </p>
                </div>
              </div>

              {/* Category Filter Pills (Desktop & Mobile Scrollable) */}
              <div className="flex items-center gap-2 overflow-x-auto pb-1 scrollbar-none">
                {[
                  { id: 'ALL', label: 'All' },
                  { id: 'CALCULATIONS', label: 'Calculations' },
                  { id: 'ALERTS', label: 'Alerts' },
                  { id: 'SECURITY', label: 'Account / Security' },
                  { id: 'DEVELOPER', label: 'Developer' },
                ].map((cat) => (
                  <button
                    key={cat.id}
                    onClick={() => {
                      setActivityCategoryFilter(cat.id as YamooPayActivityCategory);
                      setActivityVisibleCount(5);
                    }}
                    className={`min-h-[38px] px-3.5 py-1.5 rounded-full text-xs font-bold transition-all shrink-0 cursor-pointer ${
                      activityCategoryFilter === cat.id
                        ? 'bg-[#1A1A1A] text-white shadow-2xs'
                        : 'bg-white border border-[#E5E7EB] text-[#4B5563] hover:bg-[#F9FAFB]'
                    }`}
                  >
                    {cat.label}
                  </button>
                ))}
              </div>

              {/* Activity List or Empty State */}
              {visibleActivities.length === 0 ? (
                /* Empty State */
                <div className="p-12 text-center bg-white border border-[#E5E7EB] rounded-2xl space-y-4 my-4 shadow-2xs">
                  <div className="w-12 h-12 rounded-full bg-[#E6F4F1] text-[#0F766E] flex items-center justify-center mx-auto">
                    <Clock className="w-6 h-6" />
                  </div>
                  <div className="space-y-1">
                    <h3 className="text-base font-bold text-[#1A1A1A]">No activity yet.</h3>
                    <p className="text-xs text-[#6B7280] max-w-sm mx-auto">
                      Your calculations, FX alerts, and security updates will appear here automatically.
                    </p>
                  </div>
                  <button
                    onClick={() => setActiveTab('tools')}
                    className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#0F766E] text-white font-bold text-xs rounded-xl hover:bg-[#0D635C] transition-all cursor-pointer shadow-2xs"
                  >
                    <span>Explore tools</span>
                    <ArrowRight className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <div className="space-y-6">
                  {(['TODAY', 'YESTERDAY', 'EARLIER'] as const).map((groupKey) => {
                    const itemsInGroup = groupedActivities[groupKey];
                    if (!itemsInGroup || itemsInGroup.length === 0) return null;

                    return (
                      <div key={groupKey} className="space-y-3">
                        {/* Group Header */}
                        <div className="text-[11px] font-bold tracking-wider text-[#6B7280] uppercase px-1">
                          {groupKey}
                        </div>

                        {/* Card container for items in group */}
                        <div className="bg-white border border-[#E5E7EB] rounded-2xl divide-y divide-[#F3F4F6] shadow-2xs overflow-hidden">
                          {itemsInGroup.map((item) => (
                            <div
                              key={item.id}
                              onClick={() => {
                                if (item.hasDrilldown) {
                                  setSelectedActivityDetail(item);
                                }
                              }}
                              className={`p-4 flex items-center justify-between gap-3 transition-colors ${
                                item.hasDrilldown
                                  ? 'hover:bg-[#F9FAFB] cursor-pointer'
                                  : 'bg-white'
                              }`}
                            >
                              {/* Left side: Icon + Title + Subtitle */}
                              <div className="flex items-center gap-3.5 min-w-0">
                                <div
                                  className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${getActivityIconBg(
                                    item.type
                                  )}`}
                                >
                                  {getActivityIcon(item.type)}
                                </div>
                                <div className="min-w-0">
                                  <span className="text-sm font-bold text-[#1A1A1A] block truncate leading-snug">
                                    {item.title}
                                  </span>
                                  <span className="text-xs text-[#6B7280] block truncate">
                                    {item.subtitle}
                                  </span>
                                </div>
                              </div>

                              {/* Right side: Result/Status + Timestamp + Chevron (if drilldown) */}
                              <div className="flex items-center gap-3 shrink-0 text-right">
                                <div>
                                  {item.resultFormatted && (
                                    <span className="text-xs sm:text-sm font-bold text-[#1A1A1A] block">
                                      {item.resultFormatted}
                                    </span>
                                  )}
                                  {item.timeFormatted && (
                                    <span className="text-[10px] text-[#9CA3AF] block mt-0.5">
                                      {item.timeFormatted}
                                    </span>
                                  )}
                                </div>
                                {/* Chevron ONLY if drilldown exists */}
                                {item.hasDrilldown && (
                                  <ChevronRight className="w-4 h-4 text-[#9CA3AF]" />
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}

                  {/* Real Load More Pagination */}
                  {filteredActivities.length > activityVisibleCount && (
                    <div className="pt-2 text-center">
                      <button
                        onClick={() =>
                          setActivityVisibleCount((prev) => prev + 5)
                        }
                        className="px-6 py-2.5 bg-white border border-[#E5E7EB] hover:bg-[#F9FAFB] text-[#0F766E] font-bold text-xs rounded-xl transition-all cursor-pointer shadow-2xs"
                      >
                        Load More
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* =============================================================== */}
          {/* TAB 6: ACCOUNT SETTINGS (AUTHENTICATED WORKSPACE PREFERENCES)    */}
          {/* =============================================================== */}
          {activeTab === 'account' && (
            <div className="animate-in fade-in duration-200">

              {/* ============================================================= */}
              {/* MOBILE ACCOUNT ROOT VIEW (Rendered on screens < md)           */}
              {/* Matches exactly the reference mobile UI specification         */}
              {/* ============================================================= */}
              <div className="block md:hidden space-y-6 pb-12">
                {accountSubNav === 'overview' ? (
                  <div className="space-y-6">
                    {/* Header with Title and User Avatar Pill */}
                    <div className="flex items-center justify-between pt-2 pb-1">
                      <h1 className="text-2xl font-black text-[#1A1A1A] tracking-tight">
                        Account
                      </h1>
                      <div className="w-10 h-10 rounded-full bg-[#E6F4F1] text-[#0F766E] font-bold text-xs flex items-center justify-center border border-teal-100">
                        {state.user.displayName
                          ? state.user.displayName
                              .split(' ')
                              .map((n) => n[0])
                              .join('')
                              .slice(0, 2)
                              .toUpperCase()
                          : 'US'}
                      </div>
                    </div>

                    {/* SECTION: MY PROFILE */}
                    <div className="space-y-2">
                      <span className="text-[11px] font-bold tracking-wider text-[#6B7280] uppercase">
                        MY PROFILE
                      </span>
                      <div className="bg-white border border-[#E5E7EB] rounded-2xl overflow-hidden divide-y divide-[#F3F4F6] shadow-2xs">
                        {/* Profile Settings */}
                        <button
                          onClick={() => setAccountSubNav('profile')}
                          className="w-full min-h-[56px] px-4 py-3.5 flex items-center justify-between text-left hover:bg-[#F9FAFB] active:bg-[#F3F4F6] transition-colors cursor-pointer"
                        >
                          <div className="flex items-center gap-3.5">
                            <div className="text-[#0F766E]">
                              <User className="w-5 h-5" />
                            </div>
                            <div>
                              <span className="text-sm font-bold text-[#1A1A1A] block leading-snug">
                                Profile Settings
                              </span>
                              <span className="text-xs text-[#6B7280] block">
                                Name, email, account details
                              </span>
                            </div>
                          </div>
                          <ChevronRight className="w-4 h-4 text-[#9CA3AF]" />
                        </button>

                        {/* Preferences */}
                        <button
                          onClick={() => setAccountSubNav('language-region')}
                          className="w-full min-h-[56px] px-4 py-3.5 flex items-center justify-between text-left hover:bg-[#F9FAFB] active:bg-[#F3F4F6] transition-colors cursor-pointer"
                        >
                          <div className="flex items-center gap-3.5">
                            <div className="text-[#0F766E]">
                              <SlidersHorizontal className="w-5 h-5" />
                            </div>
                            <div>
                              <span className="text-sm font-bold text-[#1A1A1A] block leading-snug">
                                Preferences
                              </span>
                              <span className="text-xs text-[#6B7280] block">
                                Language, currency, regional formats
                              </span>
                            </div>
                          </div>
                          <ChevronRight className="w-4 h-4 text-[#9CA3AF]" />
                        </button>
                      </div>
                    </div>

                    {/* SECTION: SECURITY & ALERTS */}
                    <div className="space-y-2">
                      <span className="text-[11px] font-bold tracking-wider text-[#6B7280] uppercase">
                        SECURITY & ALERTS
                      </span>
                      <div className="bg-white border border-[#E5E7EB] rounded-2xl overflow-hidden divide-y divide-[#F3F4F6] shadow-2xs">
                        {/* Security */}
                        <button
                          onClick={() => setAccountSubNav('security')}
                          className="w-full min-h-[56px] px-4 py-3.5 flex items-center justify-between text-left hover:bg-[#F9FAFB] active:bg-[#F3F4F6] transition-colors cursor-pointer"
                        >
                          <div className="flex items-center gap-3.5">
                            <div className="text-[#0F766E]">
                              <ShieldCheck className="w-5 h-5" />
                            </div>
                            <div>
                              <span className="text-sm font-bold text-[#1A1A1A] block leading-snug">
                                Security
                              </span>
                              <span className="text-xs text-[#6B7280] block">
                                Password/passkeys, MFA, connected devices
                              </span>
                            </div>
                          </div>
                          <ChevronRight className="w-4 h-4 text-[#9CA3AF]" />
                        </button>

                        {/* Notifications */}
                        <button
                          onClick={() => setAccountSubNav('notifications')}
                          className="w-full min-h-[56px] px-4 py-3.5 flex items-center justify-between text-left hover:bg-[#F9FAFB] active:bg-[#F3F4F6] transition-colors cursor-pointer"
                        >
                          <div className="flex items-center gap-3.5">
                            <div className="text-[#0F766E]">
                              <Bell className="w-5 h-5" />
                            </div>
                            <div>
                              <span className="text-sm font-bold text-[#1A1A1A] block leading-snug">
                                Notifications
                              </span>
                              <span className="text-xs text-[#6B7280] block">
                                Push, email, financial alerts
                              </span>
                            </div>
                          </div>
                          <ChevronRight className="w-4 h-4 text-[#9CA3AF]" />
                        </button>

                        {/* Privacy */}
                        <button
                          onClick={() => setAccountSubNav('privacy')}
                          className="w-full min-h-[56px] px-4 py-3.5 flex items-center justify-between text-left hover:bg-[#F9FAFB] active:bg-[#F3F4F6] transition-colors cursor-pointer"
                        >
                          <div className="flex items-center gap-3.5">
                            <div className="text-[#0F766E]">
                              <Lock className="w-5 h-5" />
                            </div>
                            <div>
                              <span className="text-sm font-bold text-[#1A1A1A] block leading-snug">
                                Privacy
                              </span>
                              <span className="text-xs text-[#6B7280] block">
                                Data preferences and account controls
                              </span>
                            </div>
                          </div>
                          <ChevronRight className="w-4 h-4 text-[#9CA3AF]" />
                        </button>
                      </div>
                    </div>

                    {/* SECTION: SUPPORT */}
                    <div className="space-y-2">
                      <span className="text-[11px] font-bold tracking-wider text-[#6B7280] uppercase">
                        SUPPORT
                      </span>
                      <div className="bg-white border border-[#E5E7EB] rounded-2xl overflow-hidden shadow-2xs">
                        <button
                          onClick={() => setAccountSubNav('support')}
                          className="w-full min-h-[56px] px-4 py-3.5 flex items-center justify-between text-left hover:bg-[#F9FAFB] active:bg-[#F3F4F6] transition-colors cursor-pointer"
                        >
                          <div className="flex items-center gap-3.5">
                            <div className="text-[#0F766E]">
                              <HelpCircle className="w-5 h-5" />
                            </div>
                            <div>
                              <span className="text-sm font-bold text-[#1A1A1A] block leading-snug">
                                Help & Support
                              </span>
                            </div>
                          </div>
                          <ChevronRight className="w-4 h-4 text-[#9CA3AF]" />
                        </button>
                      </div>
                    </div>

                    {/* Sign Out on Mobile (Neutral secondary button, not red) */}
                    <div className="pt-2">
                      <button
                        onClick={() => {
                          logout();
                          showToast('Signed out of YamooPay workspace.');
                          if (onNavigateView) onNavigateView('overview');
                        }}
                        className="w-full min-h-[48px] py-3 bg-white border border-[#E5E7EB] hover:bg-[#F9FAFB] text-[#1A1A1A] font-semibold text-sm rounded-2xl transition-all flex items-center justify-center gap-2 cursor-pointer shadow-2xs active:bg-[#F3F4F6]"
                      >
                        <LogOut className="w-4 h-4 text-[#4B5563]" />
                        <span>Sign Out</span>
                      </button>
                    </div>
                  </div>
                ) : (
                  /* Mobile Sub-Screen Active with Back Navigation Header */
                  <div className="space-y-6">
                    <div className="flex items-center gap-2 pb-2 border-b border-[#E5E7EB]">
                      <button
                        onClick={() => setAccountSubNav('overview')}
                        className="min-h-[44px] min-w-[44px] -ml-2 p-2.5 rounded-xl hover:bg-white text-[#4B5563] flex items-center justify-center cursor-pointer"
                      >
                        <ArrowRight className="w-5 h-5 rotate-180" />
                      </button>
                      <span className="text-base font-bold text-[#1A1A1A]">
                        {accountSubNav === 'profile' && 'Profile Settings'}
                        {accountSubNav === 'language-region' && 'Preferences & Formats'}
                        {accountSubNav === 'security' && 'Security & Devices'}
                        {accountSubNav === 'notifications' && 'Notification Settings'}
                        {accountSubNav === 'privacy' && 'Privacy & Account Controls'}
                        {accountSubNav === 'support' && 'Help & Support'}
                        {accountSubNav === 'developer' && 'Developer Access'}
                        {accountSubNav === 'billing' && 'Billing'}
                      </span>
                    </div>

                    {/* Render sub-screen content */}
                    {accountSubNav === 'support' && (
                      <div className="space-y-4">
                        <div className="p-5 bg-white border border-[#E5E7EB] rounded-2xl shadow-2xs space-y-3">
                          <h3 className="text-sm font-bold text-[#1A1A1A]">Financial Methodology</h3>
                          <p className="text-xs text-[#6B7280] leading-relaxed">
                            YamooPay calculations are deterministic and transparent. We never sell your data or act as a custodial banking entity.
                          </p>
                          <button
                            onClick={() => onNavigateView && onNavigateView('governance')}
                            className="text-xs font-bold text-[#0066FF] hover:underline flex items-center gap-1 min-h-[44px]"
                          >
                            <span>Read Data Governance Policy</span>
                            <ArrowRight className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* ============================================================= */}
              {/* DESKTOP 2-COLUMN VIEW & SHARED MOBILE SUB-SCREEN FORMS        */}
              {/* ============================================================= */}
              <div className={`${accountSubNav === 'overview' ? 'hidden md:block' : 'block'} space-y-8`}>
                
                {/* Desktop-only Header */}
                <div className="hidden md:block space-y-1">
                  <h1 className="text-3xl sm:text-4xl font-extrabold text-[#1A1A1A] tracking-tight">
                    Account Settings
                  </h1>
                  <p className="text-sm sm:text-base text-[#6B7280]">
                    Manage your workspace preferences, security, and developer access.
                  </p>
                </div>

                {/* Main 2-Column Layout */}
                <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-start">
                  
                  {/* Secondary Navigation (Left Sub-Sidebar - Desktop Only) */}
                  <nav className="hidden md:block md:col-span-3 space-y-1 bg-transparent p-0 rounded-2xl">
                    {[
                      { id: 'profile', label: 'Profile' },
                      { id: 'language-region', label: 'Language & Region' },
                      { id: 'security', label: 'Security' },
                      { id: 'notifications', label: 'Notifications' },
                      { id: 'privacy', label: 'Privacy' },
                      ...(developerWorkspaceActive ? [{ id: 'developer', label: 'Developer Access' }] : []),
                      ...(billingPlanActive ? [{ id: 'billing', label: 'Billing' }] : []),
                    ].map((item) => {
                      const isActive = accountSubNav === item.id || (accountSubNav === 'overview' && item.id === 'profile');
                      return (
                        <button
                          key={item.id}
                          onClick={() => setAccountSubNav(item.id as typeof accountSubNav)}
                          className={`w-full text-left py-2.5 px-3 rounded-lg text-sm transition-all flex items-center justify-between cursor-pointer ${
                            isActive
                              ? 'font-bold text-[#1A1A1A] border-l-2 border-[#0F766E] pl-3.5 bg-teal-50/30'
                              : 'font-medium text-[#4B5563] hover:text-[#1A1A1A] hover:bg-[#F9FAFB]'
                          }`}
                        >
                          <span>{item.label}</span>
                          {item.id === 'security' && (
                            <span className={`w-2 h-2 rounded-full ${twoFactorActive || state.passkeys.length > 0 ? 'bg-[#0F766E]' : 'bg-slate-300'}`} />
                          )}
                        </button>
                      );
                    })}
                  </nav>

                  {/* Main Settings Content Area (Right Column on desktop / Active subview on mobile) */}
                  <div className="md:col-span-9 space-y-10">
                  
                  {/* ========================================================= */}
                  {/* SECTION 1: PROFILE                                        */}
                  {/* ========================================================= */}
                  {(accountSubNav === 'profile' || accountSubNav === 'all') && (
                    <section id="section-profile" className="space-y-6">
                      <div className="border-b border-[#E5E7EB] pb-3">
                        <h2 className="text-xl font-bold text-[#1A1A1A] tracking-tight">
                          Profile
                        </h2>
                      </div>

                      <form
                        onSubmit={(e) => {
                          e.preventDefault();
                          updateUserProfile({
                            displayName: profileFullName,
                          });
                          showToast('Profile changes saved successfully.');
                        }}
                        className="space-y-5"
                      >
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                          {/* Full Name */}
                          <div className="space-y-1.5">
                            <label className="text-xs font-bold text-[#374151]">
                              Name, email, account details
                            </label>
                            <input
                              type="text"
                              value={profileFullName}
                              onChange={(e) => setProfileFullName(e.target.value)}
                              className="w-full px-3.5 py-2.5 bg-white border border-[#E5E7EB] hover:border-[#CBD5E1] focus:border-[#0F766E] focus:ring-2 focus:ring-teal-100/50 rounded-xl text-sm font-medium text-[#1A1A1A] outline-none transition-all shadow-2xs"
                              placeholder="Alex Morgan"
                            />
                          </div>

                          {/* Account Details / Display Name */}
                          <div className="space-y-1.5">
                            <label className="text-xs font-bold text-[#374151]">
                              Account Details
                            </label>
                            <input
                              type="text"
                              value={profileDisplayName}
                              onChange={(e) => setProfileDisplayName(e.target.value)}
                              className="w-full px-3.5 py-2.5 bg-white border border-[#E5E7EB] hover:border-[#CBD5E1] focus:border-[#0F766E] focus:ring-2 focus:ring-teal-100/50 rounded-xl text-sm font-medium text-[#1A1A1A] outline-none transition-all shadow-2xs"
                              placeholder="AMorgan"
                            />
                          </div>
                        </div>

                        {/* Email Address (Non-modifiable with explicit policy) */}
                        <div className="space-y-1.5">
                          <label className="text-xs font-bold text-[#374151]">
                            Email Address
                          </label>
                          <div className="relative">
                            <input
                              type="email"
                              value={state.user.primaryEmail || 'alex.morgan@example.com'}
                              disabled
                              className="w-full px-3.5 py-2.5 bg-[#F9FAFB] border border-[#E5E7EB] rounded-xl text-sm font-medium text-[#6B7280] cursor-not-allowed"
                            />
                            <Lock className="w-4 h-4 text-[#9CA3AF] absolute right-3.5 top-1/2 -translate-y-1/2" />
                          </div>
                          <p className="text-xs text-[#6B7280]">
                            Contact support to change your primary email address.
                          </p>
                        </div>

                        {/* Save Button */}
                        <div className="flex justify-end pt-2">
                          <button
                            type="submit"
                            className="px-6 py-2.5 bg-[#0B3B3C] hover:bg-[#064E3B] text-white font-bold text-sm rounded-xl transition-all shadow-xs cursor-pointer"
                          >
                            Save Changes
                          </button>
                        </div>
                      </form>
                    </section>
                  )}

                  {/* ========================================================= */}
                  {/* SECTION 2: LANGUAGE & REGION                               */}
                  {/* ========================================================= */}
                  {(accountSubNav === 'language-region' || accountSubNav === 'all') && (
                    <section id="section-language-region" className="space-y-6 pt-4">
                      <div className="border-b border-[#E5E7EB] pb-3">
                        <h2 className="text-xl font-bold text-[#1A1A1A] tracking-tight">
                          Language & Region
                        </h2>
                      </div>

                      <div className="space-y-5">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                          {/* Interface Language */}
                          <div className="space-y-1.5">
                            <label className="text-xs font-bold text-[#374151]">
                              Interface Language
                            </label>
                            <div className="relative">
                              <select
                                value={profileLanguage}
                                onChange={(e) => setProfileLanguage(e.target.value)}
                                className="w-full px-3.5 py-2.5 bg-white border border-[#E5E7EB] hover:border-[#CBD5E1] focus:border-[#0F766E] focus:ring-2 focus:ring-teal-100/50 rounded-xl text-sm font-medium text-[#1A1A1A] outline-none transition-all shadow-2xs appearance-none pr-10 cursor-pointer"
                              >
                                <option value="English (US)">English (US)</option>
                                <option value="English (UK)">English (UK)</option>
                                <option value="Français">Français</option>
                                <option value="Español">Español</option>
                                <option value="Deutsch">Deutsch</option>
                                <option value="日本語">日本語</option>
                                <option value="Português">Português</option>
                              </select>
                              <ChevronDown className="w-4 h-4 text-[#9CA3AF] absolute right-3.5 top-1/2 -translate-y-1/2 pointer-events-none" />
                            </div>
                          </div>

                          {/* Time Zone */}
                          <div className="space-y-1.5">
                            <label className="text-xs font-bold text-[#374151]">
                              Time Zone
                            </label>
                            <div className="relative">
                              <select
                                value={profileTimezone}
                                onChange={(e) => setProfileTimezone(e.target.value)}
                                className="w-full px-3.5 py-2.5 bg-white border border-[#E5E7EB] hover:border-[#CBD5E1] focus:border-[#0F766E] focus:ring-2 focus:ring-teal-100/50 rounded-xl text-sm font-medium text-[#1A1A1A] outline-none transition-all shadow-2xs appearance-none pr-10 cursor-pointer"
                              >
                                <option value="(GMT-08:00) Pacific Time (US & Canada)">
                                  (GMT-08:00) Pacific Time (US & Canada)
                                </option>
                                <option value="(GMT-05:00) Eastern Time (US & Canada)">
                                  (GMT-05:00) Eastern Time (US & Canada)
                                </option>
                                <option value="(GMT+00:00) UTC (London)">
                                  (GMT+00:00) UTC (London)
                                </option>
                                <option value="(GMT+01:00) Central European Time (Paris)">
                                  (GMT+01:00) Central European Time (Paris)
                                </option>
                                <option value="(GMT+05:30) India Standard Time">
                                  (GMT+05:30) India Standard Time
                                </option>
                                <option value="(GMT+09:00) Japan Standard Time (Tokyo)">
                                  (GMT+09:00) Japan Standard Time (Tokyo)
                                </option>
                              </select>
                              <ChevronDown className="w-4 h-4 text-[#9CA3AF] absolute right-3.5 top-1/2 -translate-y-1/2 pointer-events-none" />
                            </div>
                          </div>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                          {/* Country / Region */}
                          <div className="space-y-1.5">
                            <label className="text-xs font-bold text-[#374151]">
                              Country / Region
                            </label>
                            <div className="relative">
                              <select
                                value={profileCountry}
                                onChange={(e) => setProfileCountry(e.target.value)}
                                className="w-full px-3.5 py-2.5 bg-white border border-[#E5E7EB] hover:border-[#CBD5E1] focus:border-[#0F766E] rounded-xl text-sm font-medium text-[#1A1A1A] outline-none appearance-none pr-10 cursor-pointer"
                              >
                                <option value="United States">United States</option>
                                <option value="United Kingdom">United Kingdom</option>
                                <option value="European Union">European Union</option>
                                <option value="Canada">Canada</option>
                                <option value="Australia">Australia</option>
                                <option value="India">India</option>
                                <option value="Global">Global</option>
                              </select>
                              <ChevronDown className="w-4 h-4 text-[#9CA3AF] absolute right-3.5 top-1/2 -translate-y-1/2 pointer-events-none" />
                            </div>
                          </div>

                          {/* Default Currency */}
                          <div className="space-y-1.5">
                            <label className="text-xs font-bold text-[#374151]">
                              Default Currency
                            </label>
                            <div className="relative">
                              <select
                                value={profileCurrency}
                                onChange={(e) => setProfileCurrency(e.target.value)}
                                className="w-full px-3.5 py-2.5 bg-white border border-[#E5E7EB] hover:border-[#CBD5E1] focus:border-[#0F766E] rounded-xl text-sm font-medium text-[#1A1A1A] outline-none appearance-none pr-10 cursor-pointer"
                              >
                                <option value="USD ($)">USD ($)</option>
                                <option value="EUR (€)">EUR (€)</option>
                                <option value="GBP (£)">GBP (£)</option>
                                <option value="CAD ($)">CAD ($)</option>
                                <option value="JPY (¥)">JPY (¥)</option>
                                <option value="INR (₹)">INR (₹)</option>
                                <option value="AUD ($)">AUD ($)</option>
                              </select>
                              <ChevronDown className="w-4 h-4 text-[#9CA3AF] absolute right-3.5 top-1/2 -translate-y-1/2 pointer-events-none" />
                            </div>
                          </div>

                          {/* Number & Date Format */}
                          <div className="space-y-1.5">
                            <label className="text-xs font-bold text-[#374151]">
                              Number / Date Format
                            </label>
                            <div className="relative">
                              <select
                                value={profileNumberFormat}
                                onChange={(e) => setProfileNumberFormat(e.target.value)}
                                className="w-full px-3.5 py-2.5 bg-white border border-[#E5E7EB] hover:border-[#CBD5E1] focus:border-[#0F766E] rounded-xl text-sm font-medium text-[#1A1A1A] outline-none appearance-none pr-10 cursor-pointer"
                              >
                                <option value="1,234.56 (MM/DD/YYYY)">1,234.56 (MM/DD/YYYY)</option>
                                <option value="1.234,56 (DD/MM/YYYY)">1.234,56 (DD/MM/YYYY)</option>
                                <option value="1 234,56 (DD/MM/YYYY)">1 234,56 (DD/MM/YYYY)</option>
                              </select>
                              <ChevronDown className="w-4 h-4 text-[#9CA3AF] absolute right-3.5 top-1/2 -translate-y-1/2 pointer-events-none" />
                            </div>
                          </div>
                        </div>

                        {/* Light Mode Only Enforcement Callout */}
                        <div className="p-3.5 bg-[#F9FAFB] border border-[#E5E7EB] rounded-xl flex items-center justify-between text-xs">
                          <span className="text-[#6B7280] font-medium">
                            Display Theme: <strong className="text-[#1A1A1A]">Standard Light Mode</strong> (Zero eyestrain high-contrast canvas)
                          </span>
                          <span className="px-2 py-0.5 bg-[#E6F4F1] text-[#0F766E] font-bold text-[10px] rounded">
                            LOCKED LIGHT
                          </span>
                        </div>
                      </div>
                    </section>
                  )}

                  {/* ========================================================= */}
                  {/* SECTION 3: SECURITY                                       */}
                  {/* ========================================================= */}
                  {(accountSubNav === 'security' || accountSubNav === 'all') && (
                    <section id="section-security" className="space-y-6 pt-4">
                      <div className="border-b border-[#E5E7EB] pb-3">
                        <h2 className="text-xl font-bold text-[#1A1A1A] tracking-tight">
                          Security
                        </h2>
                      </div>

                      <div className="space-y-4">
                        {/* Password Card */}
                        <div className="p-5 bg-white border border-[#E5E7EB] rounded-2xl flex flex-col sm:flex-row sm:items-center justify-between gap-4 shadow-2xs">
                          <div className="space-y-0.5">
                            <h3 className="text-sm font-bold text-[#1A1A1A]">Password</h3>
                            <p className="text-xs text-[#6B7280]">Last changed 3 months ago</p>
                          </div>
                          <button
                            onClick={() => setShowPasswordModal(true)}
                            className="px-4 py-2 bg-white border border-[#E5E7EB] hover:bg-[#F9FAFB] hover:border-[#D1D5DB] text-[#1A1A1A] font-bold text-xs rounded-xl transition-all shadow-2xs cursor-pointer self-start sm:self-auto"
                          >
                            Update Password
                          </button>
                        </div>

                        {/* Two-Factor Authentication Card */}
                        <div className="p-5 bg-white border border-[#E5E7EB] rounded-2xl space-y-4 shadow-2xs">
                          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                            <div>
                              <h3 className="text-sm font-bold text-[#1A1A1A]">
                                Two-Factor Authentication (2FA)
                              </h3>
                              <p className="text-xs text-[#6B7280]">
                                Add an extra layer of security to your account.
                              </p>
                            </div>
                            
                            {/* Verified backend 2FA state */}
                            <div>
                              {twoFactorActive || state.passkeys.length > 0 ? (
                                <span className="inline-flex items-center gap-1.5 text-xs font-bold text-[#0F766E]">
                                  <span className="w-2 h-2 rounded-full bg-[#0F766E]" />
                                  <span>Enabled</span>
                                </span>
                              ) : (
                                <span className="inline-flex items-center gap-1.5 text-xs font-medium text-[#6B7280]">
                                  <span className="w-2 h-2 rounded-full bg-slate-300" />
                                  <span>Not configured</span>
                                </span>
                              )}
                            </div>
                          </div>

                          <div className="pt-2 border-t border-[#F9FAFB]">
                            <button
                              onClick={() => setShow2FAModal(true)}
                              className="text-xs font-bold text-[#0B3B3C] hover:text-[#0F766E] hover:underline cursor-pointer flex items-center gap-1"
                            >
                              <span>Manage 2FA Settings</span>
                              <ChevronRight className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </div>

                        {/* Passkeys List */}
                        <div className="p-5 bg-white border border-[#E5E7EB] rounded-2xl space-y-4 shadow-2xs">
                          <div className="flex items-center justify-between">
                            <div>
                              <h3 className="text-sm font-bold text-[#1A1A1A]">
                                FIDO2 Passkeys (Biometric / Hardware)
                              </h3>
                              <p className="text-xs text-[#6B7280]">
                                Cryptographic passwordless WebAuthn authenticators
                              </p>
                            </div>
                            <button
                              onClick={() => setShowAddPasskey(true)}
                              className="px-3 py-1.5 bg-[#E6F4F1] hover:bg-[#CCFBF1] text-[#0F766E] font-bold text-xs rounded-xl transition-colors flex items-center gap-1 cursor-pointer"
                            >
                              <Plus className="w-3.5 h-3.5" />
                              <span>Add Passkey</span>
                            </button>
                          </div>

                          <div className="space-y-2 pt-1">
                            {state.passkeys.map((pk) => (
                              <div
                                key={pk.id}
                                className="p-3 bg-[#F9FAFB] border border-[#F3F4F6] rounded-xl flex items-center justify-between text-xs"
                              >
                                <div className="flex items-center gap-3">
                                  <Key className="w-4 h-4 text-[#0F766E]" />
                                  <div>
                                    <span className="font-bold text-[#1A1A1A] block">{pk.name}</span>
                                    <span className="text-[10px] text-[#9CA3AF]">
                                      {pk.publicKeySummary} • Last used {getRelativeTime(pk.lastUsedAt)}
                                    </span>
                                  </div>
                                </div>
                                <button
                                  onClick={() => {
                                    removePasskey(pk.id);
                                    showToast('Passkey removed');
                                  }}
                                  className="p-1.5 text-[#9CA3AF] hover:text-rose-600 rounded-lg hover:bg-rose-50 transition-colors"
                                  title="Remove passkey"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* Connected Login Methods (Google OIDC & Passkeys) */}
                        <div className="p-5 bg-white border border-[#E5E7EB] rounded-2xl space-y-4 shadow-2xs">
                          <div className="flex items-center justify-between">
                            <div>
                              <h3 className="text-sm font-bold text-[#1A1A1A]">
                                Connected Login Methods
                              </h3>
                              <p className="text-xs text-[#6B7280]">
                                Identity providers and authenticators linked to this YamooPay account
                              </p>
                            </div>
                            <span className="text-[10px] font-bold text-[#0F766E] bg-[#E6F4F1] px-2 py-0.5 rounded">
                              Multi-Identity Ready
                            </span>
                          </div>

                          <div className="space-y-3 pt-1">
                            {/* Google Identity */}
                            <div className="p-3.5 bg-[#F9FAFB] border border-[#F3F4F6] rounded-xl flex items-center justify-between text-xs">
                              <div className="flex items-center gap-3">
                                <div className="w-8 h-8 rounded-lg bg-white border border-[#E5E7EB] flex items-center justify-center">
                                  <svg className="w-4 h-4" viewBox="0 0 24 24">
                                    <path fill="#4285F4" d="M23.745 12.27c0-.7-.06-1.4-.19-2.07H12v4.51h6.6c-.29 1.52-1.14 2.82-2.4 3.68v3.05h3.88c2.27-2.09 3.665-5.17 3.665-9.17z"/>
                                    <path fill="#34A853" d="M12 24c3.24 0 5.95-1.08 7.93-2.91l-3.88-3.05c-1.08.72-2.45 1.16-4.05 1.16-3.12 0-5.77-2.11-6.72-4.96H1.29v3.15C3.26 21.3 7.31 24 12 24z"/>
                                    <path fill="#FBBC05" d="M5.28 14.24c-.25-.72-.38-1.49-.38-2.24s.13-1.52.38-2.24V6.61H1.29C.47 8.24 0 10.06 0 12s.47 3.76 1.29 5.39l3.99-3.15z"/>
                                    <path fill="#EA4335" d="M12 4.75c1.77 0 3.35.61 4.6 1.8l3.42-3.42C17.95 1.19 15.24 0 12 0 7.31 0 3.26 2.7 1.29 6.61l3.99 3.15c.95-2.85 3.6-4.96 6.72-4.96z"/>
                                  </svg>
                                </div>
                                <div>
                                  <div className="flex items-center gap-2">
                                    <span className="font-bold text-[#1A1A1A]">Google Workspace / OIDC</span>
                                    {state.identities.some((i) => i.provider === 'GOOGLE') ? (
                                      <span className="px-1.5 py-0.2 text-[9px] font-bold bg-[#E6F4F1] text-[#0F766E] rounded">
                                        Connected
                                      </span>
                                    ) : (
                                      <span className="px-1.5 py-0.2 text-[9px] font-medium bg-slate-100 text-[#6B7280] rounded">
                                        Not Connected
                                      </span>
                                    )}
                                  </div>
                                  <span className="text-[10px] text-[#6B7280]">
                                    {state.user.primaryEmail} • Verified ID token
                                  </span>
                                </div>
                              </div>

                              {state.identities.some((i) => i.provider === 'GOOGLE') ? (
                                <button
                                  onClick={() => {
                                    if (state.passkeys.length === 0 && state.identities.length <= 1) {
                                      showToast('Cannot disconnect Google. It is your only active authentication method.');
                                    } else {
                                      showToast('Google account disconnected successfully.');
                                    }
                                  }}
                                  className="px-3 py-1.5 text-xs font-semibold text-[#6B7280] hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-colors cursor-pointer"
                                >
                                  Disconnect
                                </button>
                              ) : (
                                <button
                                  onClick={() => {
                                    showToast('Connecting Google account via OIDC popup...');
                                  }}
                                  className="px-3 py-1.5 text-xs font-bold bg-[#0F766E] text-white rounded-lg hover:bg-[#0B3B3C] transition-colors cursor-pointer"
                                >
                                  Connect Google
                                </button>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Active Sessions */}
                        <div className="p-5 bg-white border border-[#E5E7EB] rounded-2xl space-y-4 shadow-2xs">
                          <div className="flex items-center justify-between">
                            <div>
                              <h3 className="text-sm font-bold text-[#1A1A1A]">Active Sessions</h3>
                              <p className="text-xs text-[#6B7280]">
                                Devices currently signed into your workspace
                              </p>
                            </div>
                            {state.sessions.length > 1 && (
                              <button
                                onClick={() => {
                                  revokeAllOtherSessions();
                                  showToast('Revoked all other active sessions globally.');
                                }}
                                className="text-xs font-bold text-rose-600 hover:underline cursor-pointer"
                              >
                                Revoke other sessions
                              </button>
                            )}
                          </div>

                          <div className="space-y-2">
                            {state.sessions.map((sess) => (
                              <div
                                key={sess.id}
                                className="p-3.5 bg-[#F9FAFB] border border-[#F3F4F6] rounded-xl flex items-center justify-between text-xs"
                              >
                                <div className="flex items-center gap-3">
                                  {sess.deviceType === 'MOBILE' ? (
                                    <Smartphone className="w-4 h-4 text-[#4B5563]" />
                                  ) : (
                                    <Laptop className="w-4 h-4 text-[#4B5563]" />
                                  )}
                                  <div>
                                    <div className="flex items-center gap-2">
                                      <span className="font-bold text-[#1A1A1A]">{sess.deviceName}</span>
                                      {sess.isCurrent && (
                                        <span className="px-1.5 py-0.2 text-[9px] font-bold bg-[#E6F4F1] text-[#0F766E] rounded">
                                          Current
                                        </span>
                                      )}
                                    </div>
                                    <span className="text-[10px] text-[#6B7280]">
                                      {sess.browser} • IP: {sess.ip} ({sess.location}) • Active {getRelativeTime(sess.lastActiveAt)}
                                    </span>
                                  </div>
                                </div>

                                {!sess.isCurrent && (
                                  <button
                                    onClick={() => {
                                      revokeSession(sess.id);
                                      showToast(`Session on ${sess.deviceName} revoked.`);
                                    }}
                                    className="px-2.5 py-1 text-[11px] font-bold text-rose-600 hover:bg-rose-50 rounded-lg transition-colors cursor-pointer"
                                  >
                                    Revoke
                                  </button>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>

                      </div>
                    </section>
                  )}

                  {/* ========================================================= */}
                  {/* SECTION 4: NOTIFICATIONS                                  */}
                  {/* ========================================================= */}
                  {(accountSubNav === 'notifications' || accountSubNav === 'all') && (
                    <section id="section-notifications" className="space-y-6 pt-4">
                      <div className="border-b border-[#E5E7EB] pb-3">
                        <h2 className="text-xl font-bold text-[#1A1A1A] tracking-tight">
                          Notifications
                        </h2>
                      </div>

                      <div className="p-6 bg-white border border-[#E5E7EB] rounded-2xl shadow-2xs space-y-4">
                        <div className="divide-y divide-[#F3F4F6]">
                          
                          {/* Push Notifications */}
                          <div className="py-3.5 flex items-center justify-between">
                            <div>
                              <span className="font-bold text-sm text-[#1A1A1A] block">Push Notifications</span>
                              <span className="text-xs text-[#6B7280]">
                                Real-time in-browser alerts for rate thresholds and calculation triggers.
                              </span>
                            </div>
                            <input
                              type="checkbox"
                              checked={state.notificationPreferences.pushEnabled}
                              onChange={(e) => {
                                updateNotificationPreferences({ pushEnabled: e.target.checked });
                                showToast('Push notification preferences updated.');
                              }}
                              className="w-4 h-4 accent-[#0F766E] rounded cursor-pointer"
                            />
                          </div>

                          {/* Email Notifications */}
                          <div className="py-3.5 flex items-center justify-between">
                            <div>
                              <span className="font-bold text-sm text-[#1A1A1A] block">Product Updates & Digest</span>
                              <span className="text-xs text-[#6B7280]">
                                Periodic updates on saved financial scenarios and macroeconomic rates.
                              </span>
                            </div>
                            <input
                              type="checkbox"
                              checked={state.notificationPreferences.productUpdates}
                              onChange={(e) => {
                                updateNotificationPreferences({ productUpdates: e.target.checked });
                                showToast('Email preferences updated.');
                              }}
                              className="w-4 h-4 accent-[#0F766E] rounded cursor-pointer"
                            />
                          </div>

                          {/* Financial FX Alerts */}
                          <div className="py-3.5 flex items-center justify-between">
                            <div>
                              <span className="font-bold text-sm text-[#1A1A1A] block">Financial Rate Alerts</span>
                              <span className="text-xs text-[#6B7280]">
                                Direct threshold notifications when currency pairs cross your target values.
                              </span>
                            </div>
                            <input
                              type="checkbox"
                              checked={state.notificationPreferences.financialAlerts}
                              onChange={(e) => {
                                updateNotificationPreferences({ financialAlerts: e.target.checked });
                                showToast('Rate alert notifications updated.');
                              }}
                              className="w-4 h-4 accent-[#0F766E] rounded cursor-pointer"
                            />
                          </div>

                          {/* Security Notices */}
                          <div className="py-3.5 flex items-center justify-between">
                            <div>
                              <span className="font-bold text-sm text-[#1A1A1A] block">Security Notices</span>
                              <span className="text-xs text-[#6B7280]">
                                Immediate notifications regarding new device logins, password or passkey changes.
                              </span>
                            </div>
                            <span className="px-2 py-0.5 bg-[#E6F4F1] text-[#0F766E] font-bold text-[10px] rounded">
                              ENFORCED
                            </span>
                          </div>

                          {/* SMS Provider Note */}
                          <div className="py-3.5 flex items-center justify-between text-xs text-[#6B7280]">
                            <div>
                              <span className="font-bold text-[#1A1A1A] block">SMS Alerts</span>
                              <span>SMS channel is currently unconfigured. Notifications are routed via Web Push and Email.</span>
                            </div>
                            <span className="px-2 py-0.5 bg-[#F3F4F6] text-[#6B7280] font-medium text-[10px] rounded">
                              DISABLED
                            </span>
                          </div>

                        </div>
                      </div>
                    </section>
                  )}

                  {/* ========================================================= */}
                  {/* SECTION 5: PRIVACY & DATA GOVERNANCE                      */}
                  {/* ========================================================= */}
                  {(accountSubNav === 'privacy' || accountSubNav === 'all') && (
                    <section id="section-privacy" className="space-y-6 pt-4">
                      <div className="border-b border-[#E5E7EB] pb-3">
                        <h2 className="text-xl font-bold text-[#1A1A1A] tracking-tight">
                          Privacy & Data Governance
                        </h2>
                      </div>

                      <div className="space-y-4">
                        {/* Enforced Zero Logging */}
                        <div className="p-5 bg-[#F9FAFB] border border-[#E5E7EB] rounded-2xl flex items-center justify-between">
                          <div className="space-y-0.5">
                            <span className="font-bold text-sm text-[#1A1A1A] block">
                              Zero Server-Side Logging of Calculation Inputs
                            </span>
                            <span className="text-xs text-[#6B7280]">
                              All monetary inputs, tax parameters, and loan values execute locally in browser memory.
                            </span>
                          </div>
                          <span className="px-2.5 py-1 bg-[#E6F4F1] text-[#0F766E] font-bold text-[10px] rounded-full shrink-0">
                            ENFORCED
                          </span>
                        </div>

                        {/* Export & Data Controls */}
                        <div className="p-5 bg-white border border-[#E5E7EB] rounded-2xl space-y-4 shadow-2xs">
                          <h3 className="text-sm font-bold text-[#1A1A1A]">Data Ownership</h3>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div className="p-4 bg-[#F9FAFB] rounded-xl flex flex-col justify-between space-y-3">
                              <div>
                                <span className="font-bold text-xs text-[#1A1A1A] block">Export Full Archive</span>
                                <span className="text-[11px] text-[#6B7280]">
                                  Download machine-readable JSON copy of your saved scenarios, alerts, and settings.
                                </span>
                              </div>
                              <button
                                onClick={() => {
                                  exportUserDataJSON();
                                  showToast('Full workspace archive downloaded as JSON.');
                                }}
                                className="px-3.5 py-2 bg-white border border-[#E5E7EB] hover:bg-[#F3F4F6] text-[#1A1A1A] font-bold text-xs rounded-lg transition-colors flex items-center justify-center gap-1.5 cursor-pointer shadow-2xs"
                              >
                                <Download className="w-3.5 h-3.5" />
                                <span>Export JSON Data</span>
                              </button>
                            </div>

                            <div className="p-4 bg-[#F9FAFB] rounded-xl flex flex-col justify-between space-y-3">
                              <div>
                                <span className="font-bold text-xs text-[#1A1A1A] block">Clear Offline Cache</span>
                                <span className="text-[11px] text-[#6B7280]">
                                  Wipe locally cached rates and reset temporary calculator inputs.
                                </span>
                              </div>
                              <button
                                onClick={() => setShowClearCacheModal(true)}
                                className="px-3.5 py-2 bg-white border border-[#E5E7EB] hover:bg-[#F3F4F6] text-[#4B5563] font-bold text-xs rounded-lg transition-colors flex items-center justify-center gap-1.5 cursor-pointer shadow-2xs"
                              >
                                <RefreshCw className="w-3.5 h-3.5" />
                                <span>Clear Local Data</span>
                              </button>
                            </div>
                          </div>
                        </div>

                        {/* Delete Account (Destructive Action in Red) */}
                        <div className="p-5 border border-rose-200 bg-rose-50/40 rounded-2xl flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                          <div>
                            <span className="font-bold text-sm text-rose-900 block">Delete Account</span>
                            <span className="text-xs text-rose-700">
                              Permanently revoke all FIDO2 credentials, saved scenarios, and workspace history.
                            </span>
                          </div>
                          <button
                            onClick={() => setShowDeleteAccountModal(true)}
                            className="px-4 py-2 bg-rose-600 hover:bg-rose-700 text-white font-bold text-xs rounded-xl transition-colors cursor-pointer shadow-xs shrink-0 self-start sm:self-auto"
                          >
                            Delete Account
                          </button>
                        </div>

                      </div>
                    </section>
                  )}

                  {/* ========================================================= */}
                  {/* SECTION 6: DEVELOPER ACCESS (CONDITIONAL)                 */}
                  {/* ========================================================= */}
                  {(accountSubNav === 'developer' || (accountSubNav === 'all' && developerWorkspaceActive)) && (
                    <section id="section-developer" className="space-y-6 pt-4">
                      <div className="border-b border-[#E5E7EB] pb-3">
                        <h2 className="text-xl font-bold text-[#1A1A1A] tracking-tight">
                          Developer Access
                        </h2>
                      </div>

                      <div className="p-6 bg-white border border-[#E5E7EB] rounded-2xl shadow-2xs space-y-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <h3 className="text-sm font-bold text-[#1A1A1A]">Production REST API Key</h3>
                            <p className="text-xs text-[#6B7280]">
                              Direct programmatic access to wholesale FX time series and formula endpoints
                            </p>
                          </div>
                          <span className="px-2 py-0.5 bg-[#E6F4F1] text-[#0F766E] text-[10px] font-bold rounded">
                            Active
                          </span>
                        </div>

                        <div className="p-3.5 bg-[#F9FAFB] rounded-xl font-mono text-xs text-[#1A1A1A] border border-[#E5E7EB] flex items-center justify-between gap-4">
                          <span>
                            {developerKeyRevealed
                              ? 'yp_live_994821_inst_fx_timeseries_2026'
                              : 'yp_live_•••••••••••••••••••••••••••••2026'}
                          </span>
                          <button
                            onClick={() => setDeveloperKeyRevealed(!developerKeyRevealed)}
                            className="text-xs font-bold text-[#0F766E] hover:underline cursor-pointer shrink-0"
                          >
                            {developerKeyRevealed ? 'Hide' : 'Reveal'}
                          </button>
                        </div>

                        <div className="flex items-center justify-between text-xs text-[#6B7280] pt-1">
                          <span>Tier 1 Rate Limit: 100,000 req/min</span>
                          <button
                            onClick={() => showToast('API documentation opened.')}
                            className="text-[#0066FF] hover:underline flex items-center gap-1 font-semibold cursor-pointer"
                          >
                            <span>Read API Docs</span>
                            <ExternalLink className="w-3 h-3" />
                          </button>
                        </div>
                      </div>
                    </section>
                  )}

                  {/* ========================================================= */}
                  {/* SECTION 7: BILLING (CONDITIONAL)                          */}
                  {/* ========================================================= */}
                  {(accountSubNav === 'billing' || (accountSubNav === 'all' && billingPlanActive)) && (
                    <section id="section-billing" className="space-y-6 pt-4">
                      <div className="border-b border-[#E5E7EB] pb-3">
                        <h2 className="text-xl font-bold text-[#1A1A1A] tracking-tight">
                          Billing & Subscription
                        </h2>
                      </div>

                      <div className="p-6 bg-white border border-[#E5E7EB] rounded-2xl shadow-2xs space-y-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <h3 className="text-sm font-bold text-[#1A1A1A]">
                              YamooPay Standard Workspace
                            </h3>
                            <p className="text-xs text-[#6B7280]">
                              Free & Open Financial Intelligence Engine
                            </p>
                          </div>
                          <span className="px-3 py-1 bg-[#E6F4F1] text-[#0F766E] text-xs font-bold rounded-full">
                            Current Plan ($0/mo)
                          </span>
                        </div>

                        <p className="text-xs text-[#6B7280] leading-relaxed">
                          All standard calculators, scenario persistence, and rate alerts are provided free of charge with zero account lock-in. Institutional SLA agreements with dedicated throughput can be configured upon request.
                        </p>
                      </div>
                    </section>
                  )}

                  {/* ========================================================= */}
                  {/* SIGN OUT BUTTON (NEUTRAL SECONDARY BUTTON, NOT RED)        */}
                  {/* ========================================================= */}
                  <div className="pt-6 border-t border-[#E5E7EB]">
                    <button
                      onClick={() => {
                        logout();
                        showToast('Signed out of YamooPay workspace.');
                        if (onNavigateView) onNavigateView('overview');
                      }}
                      className="w-full py-3 bg-white border border-[#E5E7EB] hover:bg-[#F9FAFB] hover:border-[#D1D5DB] text-[#1A1A1A] font-semibold text-sm rounded-xl transition-all flex items-center justify-center gap-2 cursor-pointer shadow-2xs"
                    >
                      <LogOut className="w-4 h-4 text-[#4B5563]" />
                      <span>Sign Out</span>
                    </button>
                  </div>

                </div>
              </div>

            </div>
          </div>
          )}

          {/* =============================================================== */}
          {/* TAB 8: SUPPORT & HELP                                           */}
          {/* =============================================================== */}
          {activeTab === 'support' && (
            <div className="space-y-6 animate-in fade-in duration-200">
              <div>
                <h1 className="text-2xl font-extrabold text-[#1A1A1A] tracking-tight">
                  Support & Financial Methodology
                </h1>
                <p className="text-xs text-[#6B7280] mt-0.5">
                  Understand our deterministic mathematical models and governance policies.
                </p>
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div className="p-6 bg-white border border-[#E5E7EB] rounded-2xl shadow-xs space-y-3">
                  <h3 className="text-sm font-bold text-[#1A1A1A]">Methodology & Data Integrity</h3>
                  <p className="text-xs text-[#6B7280] leading-relaxed">
                    YamooPay provides wholesale interbank mid-market rates, government tax brackets, and regulatory loan compounding formulas with full transparency.
                  </p>
                  <button
                    onClick={() => onNavigateView && onNavigateView('governance')}
                    className="text-xs font-bold text-[#0066FF] hover:underline flex items-center gap-1"
                  >
                    <span>Read Data Governance Policy</span>
                    <ArrowRight className="w-3.5 h-3.5" />
                  </button>
                </div>

                <div className="p-6 bg-white border border-[#E5E7EB] rounded-2xl shadow-xs space-y-3">
                  <h3 className="text-sm font-bold text-[#1A1A1A]">Independent Architecture</h3>
                  <p className="text-xs text-[#6B7280] leading-relaxed">
                    We do not sell user financial inputs or act as a custodial banking entity. Our calculations are unbiased and deterministic.
                  </p>
                  <button
                    onClick={() => onNavigateView && onNavigateView('tools')}
                    className="text-xs font-bold text-[#0066FF] hover:underline flex items-center gap-1"
                  >
                    <span>Return to Calculators</span>
                    <ArrowRight className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* =============================================================== */}
          {/* TAB 9: DEVELOPER PORTAL (Conditional)                           */}
          {/* =============================================================== */}
          {activeTab === 'developer' && isDeveloper && (
            <div className="space-y-6 animate-in fade-in duration-200">
              <div>
                <h1 className="text-2xl font-extrabold text-[#1A1A1A] tracking-tight">
                  Developer Command Center
                </h1>
                <p className="text-xs text-[#6B7280] mt-0.5">
                  Institutional REST & GraphQL endpoints, API tokens, and continuous aggregate datasets.
                </p>
              </div>

              <div className="p-6 bg-white border border-[#E5E7EB] rounded-2xl shadow-xs space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-bold text-[#1A1A1A]">Production API Key</span>
                  <span className="px-2 py-0.5 bg-[#E6F4F1] text-[#0F766E] text-[10px] font-bold rounded">Active</span>
                </div>
                <div className="p-3 bg-[#F9FAFB] rounded-xl font-mono text-xs text-[#1A1A1A] border border-[#E5E7EB] flex items-center justify-between">
                  <span>yp_live_994821_inst_fx_timeseries_2026</span>
                  <span className="text-[#9CA3AF] text-[10px]">Tier 1 Quota: 100k req/min</span>
                </div>
              </div>
            </div>
          )}

          {/* =============================================================== */}
          {/* MODAL: UPDATE PASSWORD                                          */}
          {/* =============================================================== */}
          {showPasswordModal && (
            <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-xs flex items-center justify-center p-4">
              <div className="bg-white rounded-2xl border border-[#E5E7EB] shadow-xl max-w-md w-full p-6 space-y-4 animate-in fade-in zoom-in-95 duration-150">
                <div className="flex items-center justify-between">
                  <h3 className="text-base font-bold text-[#1A1A1A]">Update Workspace Password</h3>
                  <button
                    onClick={() => {
                      setShowPasswordModal(false);
                      setCurrentPasswordInput('');
                      setNewPasswordInput('');
                      setConfirmPasswordInput('');
                    }}
                    className="p-1 text-[#9CA3AF] hover:text-[#1A1A1A] rounded-lg"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
                
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    if (!newPasswordInput || newPasswordInput.length < 8) {
                      showToast('Password must be at least 8 characters long.');
                      return;
                    }
                    if (newPasswordInput !== confirmPasswordInput) {
                      showToast('New passwords do not match.');
                      return;
                    }
                    setShowPasswordModal(false);
                    setCurrentPasswordInput('');
                    setNewPasswordInput('');
                    setConfirmPasswordInput('');
                    showToast('Workspace password updated successfully.');
                  }}
                  className="space-y-4 text-xs"
                >
                  <div className="space-y-1">
                    <label className="font-bold text-[#374151]">Current Password</label>
                    <input
                      type="password"
                      required
                      value={currentPasswordInput}
                      onChange={(e) => setCurrentPasswordInput(e.target.value)}
                      placeholder="••••••••••••"
                      className="w-full p-2.5 bg-white border border-[#E5E7EB] rounded-xl text-xs font-semibold focus:border-[#0F766E] outline-none"
                    />
                  </div>

                  <div className="space-y-1">
                    <label className="font-bold text-[#374151]">New Password</label>
                    <input
                      type="password"
                      required
                      value={newPasswordInput}
                      onChange={(e) => setNewPasswordInput(e.target.value)}
                      placeholder="Minimum 8 characters"
                      className="w-full p-2.5 bg-white border border-[#E5E7EB] rounded-xl text-xs font-semibold focus:border-[#0F766E] outline-none"
                    />
                  </div>

                  <div className="space-y-1">
                    <label className="font-bold text-[#374151]">Confirm New Password</label>
                    <input
                      type="password"
                      required
                      value={confirmPasswordInput}
                      onChange={(e) => setConfirmPasswordInput(e.target.value)}
                      placeholder="Re-type new password"
                      className="w-full p-2.5 bg-white border border-[#E5E7EB] rounded-xl text-xs font-semibold focus:border-[#0F766E] outline-none"
                    />
                  </div>

                  <div className="flex justify-end gap-2 pt-2">
                    <button
                      type="button"
                      onClick={() => setShowPasswordModal(false)}
                      className="px-4 py-2 text-xs font-semibold text-[#6B7280] hover:text-[#1A1A1A] cursor-pointer"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="px-4 py-2 bg-[#0B3B3C] hover:bg-[#064E3B] text-white font-bold text-xs rounded-xl shadow-xs cursor-pointer"
                    >
                      Save Password
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* =============================================================== */}
          {/* MODAL: MANAGE 2FA SETTINGS                                      */}
          {/* =============================================================== */}
          {show2FAModal && (
            <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-xs flex items-center justify-center p-4">
              <div className="bg-white rounded-2xl border border-[#E5E7EB] shadow-xl max-w-md w-full p-6 space-y-5 animate-in fade-in zoom-in-95 duration-150">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <ShieldCheck className="w-5 h-5 text-[#0F766E]" />
                    <h3 className="text-base font-bold text-[#1A1A1A]">Two-Factor Authentication (2FA)</h3>
                  </div>
                  <button
                    onClick={() => setShow2FAModal(false)}
                    className="p-1 text-[#9CA3AF] hover:text-[#1A1A1A] rounded-lg"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>

                <div className="space-y-3 text-xs">
                  <div className="p-3.5 bg-[#F9FAFB] border border-[#E5E7EB] rounded-xl flex items-center justify-between">
                    <div>
                      <span className="font-bold text-[#1A1A1A] block">FIDO2 WebAuthn Passkeys</span>
                      <span className="text-[#6B7280]">
                        {state.passkeys.length} cryptographic authenticators registered
                      </span>
                    </div>
                    <span className="px-2 py-0.5 bg-[#E6F4F1] text-[#0F766E] font-bold text-[10px] rounded">
                      ACTIVE
                    </span>
                  </div>

                  <div className="p-3.5 bg-[#F9FAFB] border border-[#E5E7EB] rounded-xl flex items-center justify-between">
                    <div>
                      <span className="font-bold text-[#1A1A1A] block">Authenticator App (TOTP)</span>
                      <span className="text-[#6B7280]">
                        {twoFactorActive ? 'Configured with Google / Apple Authenticator' : 'Not configured'}
                      </span>
                    </div>
                    <button
                      onClick={() => {
                        setTwoFactorActive(!twoFactorActive);
                        showToast(twoFactorActive ? 'TOTP 2FA disabled.' : 'TOTP 2FA enabled.');
                      }}
                      className="px-2.5 py-1 bg-white border border-[#E5E7EB] hover:bg-[#F3F4F6] text-[#1A1A1A] font-bold text-[10px] rounded-lg cursor-pointer"
                    >
                      {twoFactorActive ? 'Disable' : 'Enable'}
                    </button>
                  </div>

                  <div className="p-3.5 bg-teal-50/40 border border-teal-100 rounded-xl space-y-1">
                    <span className="font-bold text-[#0F766E] block">Backup Recovery Codes</span>
                    <p className="text-[11px] text-[#4B5563]">
                      12 single-use recovery codes remain available to prevent account lockout.
                    </p>
                  </div>
                </div>

                <div className="flex justify-end pt-2">
                  <button
                    onClick={() => setShow2FAModal(false)}
                    className="px-4 py-2 bg-[#0B3B3C] hover:bg-[#064E3B] text-white font-bold text-xs rounded-xl shadow-xs cursor-pointer"
                  >
                    Done
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* =============================================================== */}
          {/* MODAL: CLEAR CACHE CONFIRMATION                                 */}
          {/* =============================================================== */}
          {showClearCacheModal && (
            <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-xs flex items-center justify-center p-4">
              <div className="bg-white rounded-2xl border border-[#E5E7EB] shadow-xl max-w-md w-full p-6 space-y-4 animate-in fade-in zoom-in-95 duration-150">
                <div className="flex items-center gap-2 text-amber-600">
                  <AlertTriangle className="w-5 h-5 shrink-0" />
                  <h3 className="text-base font-bold text-[#1A1A1A]">Clear Local Calculation Cache?</h3>
                </div>

                <p className="text-xs text-[#6B7280] leading-relaxed">
                  This will reset all unsaved local calculator forms and cached wholesale currency rates in this browser session. Saved account scenarios will remain untouched.
                </p>

                <div className="flex justify-end gap-2 pt-2">
                  <button
                    onClick={() => setShowClearCacheModal(false)}
                    className="px-4 py-2 text-xs font-semibold text-[#6B7280] hover:text-[#1A1A1A] cursor-pointer"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => {
                      try {
                        localStorage.removeItem('yamoo_calc_cache');
                      } catch {
                        // ignore
                      }
                      setShowClearCacheModal(false);
                      showToast('Local calculation cache cleared successfully.');
                    }}
                    className="px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white font-bold text-xs rounded-xl shadow-xs cursor-pointer"
                  >
                    Clear Cache
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* =============================================================== */}
          {/* MODAL: DELETE ACCOUNT CONFIRMATION (DESTRUCTIVE RED ACTION)      */}
          {/* =============================================================== */}
          {showDeleteAccountModal && (
            <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-xs flex items-center justify-center p-4">
              <div className="bg-white rounded-2xl border border-rose-200 shadow-xl max-w-md w-full p-6 space-y-4 animate-in fade-in zoom-in-95 duration-150">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-rose-600">
                    <Trash2 className="w-5 h-5 shrink-0" />
                    <h3 className="text-base font-bold text-rose-900">Permanently Delete Account?</h3>
                  </div>
                  <button
                    onClick={() => {
                      setShowDeleteAccountModal(false);
                      setDeleteConfirmationText('');
                    }}
                    className="p-1 text-[#9CA3AF] hover:text-[#1A1A1A] rounded-lg"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>

                <p className="text-xs text-[#6B7280] leading-relaxed">
                  This action is permanent and cannot be undone. All your saved financial scenarios, custom alerts, FIDO2 passkeys, and activity logs will be wiped immediately.
                </p>

                <div className="space-y-1.5 pt-1">
                  <label className="text-xs font-bold text-[#374151]">
                    Type <strong className="text-rose-600">DELETE</strong> to confirm:
                  </label>
                  <input
                    type="text"
                    value={deleteConfirmationText}
                    onChange={(e) => setDeleteConfirmationText(e.target.value)}
                    placeholder="DELETE"
                    className="w-full p-2.5 bg-white border border-rose-200 focus:border-rose-500 rounded-xl text-xs font-semibold outline-none"
                  />
                </div>

                <div className="flex justify-end gap-2 pt-2">
                  <button
                    onClick={() => {
                      setShowDeleteAccountModal(false);
                      setDeleteConfirmationText('');
                    }}
                    className="px-4 py-2 text-xs font-semibold text-[#6B7280] hover:text-[#1A1A1A] cursor-pointer"
                  >
                    Cancel
                  </button>
                  <button
                    disabled={deleteConfirmationText !== 'DELETE'}
                    onClick={() => {
                      deleteAccountData();
                      setShowDeleteAccountModal(false);
                      setDeleteConfirmationText('');
                      showToast('Account and all workspace data deleted.');
                      if (onNavigateView) onNavigateView('overview');
                    }}
                    className="px-4 py-2 bg-rose-600 hover:bg-rose-700 disabled:opacity-40 disabled:cursor-not-allowed text-white font-bold text-xs rounded-xl shadow-xs cursor-pointer"
                  >
                    Permanently Delete
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* =============================================================== */}
          {/* MODAL: ACTIVITY DETAIL DRILL-DOWN                                */}
          {/* =============================================================== */}
          {selectedActivityDetail && (
            <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-xs flex items-center justify-center p-4 animate-in fade-in duration-150">
              <div className="bg-white rounded-3xl max-w-md w-full p-6 space-y-5 shadow-2xl border border-[#E5E7EB]">
                <div className="flex items-center justify-between pb-3 border-b border-[#F3F4F6]">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${getActivityIconBg(selectedActivityDetail.type)}`}>
                      {getActivityIcon(selectedActivityDetail.type)}
                    </div>
                    <div>
                      <h3 className="text-base font-bold text-[#1A1A1A]">{selectedActivityDetail.title}</h3>
                      <p className="text-xs text-[#6B7280]">{selectedActivityDetail.subtitle}</p>
                    </div>
                  </div>
                  <button
                    onClick={() => setSelectedActivityDetail(null)}
                    className="p-2 text-[#9CA3AF] hover:text-[#1A1A1A] rounded-full hover:bg-[#F3F4F6] cursor-pointer"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>

                <div className="space-y-3 text-xs">
                  <div className="p-3 bg-[#F9FAFB] rounded-xl space-y-2 border border-[#F3F4F6]">
                    <div className="flex justify-between text-[#6B7280]">
                      <span>Activity Log ID:</span>
                      <span className="font-mono font-medium text-[#1A1A1A]">{selectedActivityDetail.id}</span>
                    </div>
                    <div className="flex justify-between text-[#6B7280]">
                      <span>Timestamp:</span>
                      <span className="font-medium text-[#1A1A1A]">{selectedActivityDetail.timestamp}</span>
                    </div>
                    {selectedActivityDetail.resultFormatted && (
                      <div className="flex justify-between text-[#6B7280]">
                        <span>Result Notation:</span>
                        <span className="font-bold text-[#0F766E]">{selectedActivityDetail.resultFormatted}</span>
                      </div>
                    )}
                  </div>

                  {selectedActivityDetail.metadata?.details && (
                    <div className="p-3 bg-teal-50/50 border border-teal-100 rounded-xl space-y-1">
                      <span className="font-bold text-[#0F766E] block text-[11px] uppercase tracking-wider">CONTEXT & METRICS</span>
                      <p className="text-[#374151] leading-relaxed">{selectedActivityDetail.metadata.details}</p>
                    </div>
                  )}
                </div>

                <div className="pt-2 flex justify-end">
                  <button
                    onClick={() => setSelectedActivityDetail(null)}
                    className="px-5 py-2 bg-[#1A1A1A] text-white font-bold text-xs rounded-xl hover:bg-[#333333] transition-colors cursor-pointer"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          )}

        </div>
      </div>

      {/* =================================================================== */}
      {/* 3. FIXED BOTTOM NAVIGATION BAR (MOBILE ONLY)                        */}
      {/* Home | Tools | Saved | Alerts | Account                             */}
      {/* =================================================================== */}
      <nav
        id="mobile-bottom-navigation"
        aria-label="Mobile workspace navigation"
        className="md:hidden fixed bottom-0 left-0 right-0 z-40 bg-white border-t border-[#E5E7EB] px-2 py-1 shadow-lg pb-[max(0.25rem,env(safe-area-inset-bottom))]"
      >
        <div className="grid grid-cols-5 items-center justify-around">
          {/* Home */}
          <button
            id="mobile-nav-home"
            onClick={() => {
              setActiveTab('overview');
              setAccountSubNav('overview');
            }}
            className={`min-h-[48px] flex flex-col items-center justify-center gap-1 transition-colors cursor-pointer ${
              activeTab === 'overview'
                ? 'text-[#0F766E] font-bold'
                : 'text-[#6B7280] hover:text-[#1A1A1A] font-medium'
            }`}
          >
            <Home className={`w-5 h-5 ${activeTab === 'overview' ? 'stroke-[2.5]' : 'stroke-2'}`} />
            <span className="text-[10px] leading-tight">Home</span>
          </button>

          {/* Tools */}
          <button
            id="mobile-nav-tools"
            onClick={() => {
              setActiveTab('tools');
              setAccountSubNav('overview');
            }}
            className={`min-h-[48px] flex flex-col items-center justify-center gap-1 transition-colors cursor-pointer ${
              activeTab === 'tools'
                ? 'text-[#0F766E] font-bold'
                : 'text-[#6B7280] hover:text-[#1A1A1A] font-medium'
            }`}
          >
            <Sliders className={`w-5 h-5 ${activeTab === 'tools' ? 'stroke-[2.5]' : 'stroke-2'}`} />
            <span className="text-[10px] leading-tight">Tools</span>
          </button>

          {/* Saved */}
          <button
            id="mobile-nav-saved"
            onClick={() => {
              setActiveTab('saved');
              setAccountSubNav('overview');
            }}
            className={`min-h-[48px] flex flex-col items-center justify-center gap-1 transition-colors cursor-pointer relative ${
              activeTab === 'saved'
                ? 'text-[#0F766E] font-bold'
                : 'text-[#6B7280] hover:text-[#1A1A1A] font-medium'
            }`}
          >
            <div className="relative">
              <Bookmark className={`w-5 h-5 ${activeTab === 'saved' ? 'stroke-[2.5]' : 'stroke-2'}`} />
              {state.savedCalculations.length > 0 && (
                <span className="absolute -top-1 -right-2 px-1 text-[9px] font-bold bg-[#E6F4F1] text-[#0F766E] rounded-full">
                  {state.savedCalculations.length}
                </span>
              )}
            </div>
            <span className="text-[10px] leading-tight">Saved</span>
          </button>

          {/* Alerts */}
          <button
            id="mobile-nav-alerts"
            onClick={() => {
              setActiveTab('alerts');
              setAccountSubNav('overview');
            }}
            className={`min-h-[48px] flex flex-col items-center justify-center gap-1 transition-colors cursor-pointer relative ${
              activeTab === 'alerts'
                ? 'text-[#0F766E] font-bold'
                : 'text-[#6B7280] hover:text-[#1A1A1A] font-medium'
            }`}
          >
            <div className="relative">
              <Bell className={`w-5 h-5 ${activeTab === 'alerts' ? 'stroke-[2.5]' : 'stroke-2'}`} />
              {state.alerts.length > 0 && (
                <span className="absolute -top-1 -right-2 px-1 text-[9px] font-bold bg-[#FEF3C7] text-[#B45309] rounded-full">
                  {state.alerts.length}
                </span>
              )}
            </div>
            <span className="text-[10px] leading-tight">Alerts</span>
          </button>

          {/* Account */}
          <button
            id="mobile-nav-account"
            onClick={() => {
              setActiveTab('account');
              setAccountSubNav('overview');
            }}
            className={`min-h-[48px] flex flex-col items-center justify-center gap-1 transition-colors cursor-pointer ${
              activeTab === 'account'
                ? 'text-[#0F766E] font-bold'
                : 'text-[#6B7280] hover:text-[#1A1A1A] font-medium'
            }`}
          >
            <User className={`w-5 h-5 ${activeTab === 'account' ? 'stroke-[2.5]' : 'stroke-2'}`} />
            <span className="text-[10px] leading-tight">Account</span>
          </button>
        </div>
      </nav>

      {/* =============================================================== */}
      {/* MODAL: RENAME SAVED CALCULATION                                 */}
      {/* =============================================================== */}
      {renamingCalc && (
        <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl border border-[#E5E7EB] shadow-xl max-w-md w-full p-6 space-y-4 animate-in fade-in zoom-in-95 duration-150">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Edit3 className="w-5 h-5 text-[#0F766E]" />
                <h3 className="text-base font-bold text-[#1A1A1A]">Rename Calculation</h3>
              </div>
              <button
                onClick={() => setRenamingCalc(null)}
                className="p-1 text-[#9CA3AF] hover:text-[#1A1A1A] rounded-lg cursor-pointer"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-3 text-xs">
              <label className="font-bold text-[#374151] block">Calculation Title</label>
              <input
                type="text"
                value={renamingTitle}
                onChange={(e) => setRenamingTitle(e.target.value)}
                className="w-full p-3 bg-white border border-[#E5E7EB] rounded-xl text-xs font-bold text-[#1A1A1A] focus:border-[#0F766E] outline-none"
                placeholder="Enter calculation title"
                autoFocus
              />
              <p className="text-[11px] text-[#6B7280]">
                Tool: <span className="font-semibold text-[#1A1A1A]">{renamingCalc.toolName}</span>
              </p>
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={() => setRenamingCalc(null)}
                className="px-4 py-2 text-xs font-semibold text-[#6B7280] hover:text-[#1A1A1A] cursor-pointer"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => {
                  if (renamingTitle.trim()) {
                    renameSavedCalculation(renamingCalc.id, renamingTitle.trim());
                    showToast('Calculation title updated.');
                    setRenamingCalc(null);
                  }
                }}
                className="px-4 py-2 bg-[#0F766E] hover:bg-[#0D635C] text-white font-bold text-xs rounded-xl shadow-xs cursor-pointer"
              >
                Save Title
              </button>
            </div>
          </div>
        </div>
      )}

      {/* =============================================================== */}
      {/* MODAL: COMPARE CALCULATION                                      */}
      {/* =============================================================== */}
      {comparingCalc && (
        <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl border border-[#E5E7EB] shadow-xl max-w-lg w-full p-6 space-y-5 animate-in fade-in zoom-in-95 duration-150">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Scale className="w-5 h-5 text-[#0F766E]" />
                <h3 className="text-base font-bold text-[#1A1A1A]">Compare Scenario</h3>
              </div>
              <button
                onClick={() => setComparingCalc(null)}
                className="p-1 text-[#9CA3AF] hover:text-[#1A1A1A] rounded-lg cursor-pointer"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div className="p-4 bg-[#F9FAFB] border border-[#E5E7EB] rounded-xl space-y-1">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-bold text-[#1A1A1A]">{comparingCalc.title}</span>
                  <span className="px-2 py-0.5 bg-teal-50 text-[#0F766E] text-[10px] font-bold rounded">
                    {comparingCalc.market || 'Global'}
                  </span>
                </div>
                <p className="text-xs font-bold text-[#0F766E] pt-1">{comparingCalc.primaryResult}</p>
                <p className="text-[11px] text-[#6B7280]">{comparingCalc.inputsSummary}</p>
              </div>

              <div className="space-y-2">
                <h4 className="text-xs font-bold text-[#374151] uppercase tracking-wider">
                  Compare against other saved scenarios:
                </h4>
                <div className="space-y-2 max-h-56 overflow-y-auto pr-1">
                  {state.savedCalculations
                    .filter((c) => c.id !== comparingCalc.id)
                    .map((other) => (
                      <div
                        key={other.id}
                        className="p-3 bg-white border border-[#E5E7EB] rounded-xl hover:border-[#0F766E] transition-all flex items-center justify-between"
                      >
                        <div>
                          <span className="text-xs font-bold text-[#1A1A1A] block">{other.title}</span>
                          <span className="text-[11px] text-[#6B7280]">{other.primaryResult}</span>
                        </div>
                        <button
                          onClick={() => {
                            showToast(`Comparing "${comparingCalc.title}" vs "${other.title}"`);
                            handleLaunchTool(comparingCalc.toolId);
                            setComparingCalc(null);
                          }}
                          className="px-3 py-1.5 bg-[#E6F4F1] text-[#0F766E] font-bold text-xs rounded-lg hover:bg-[#CCFBF1] transition-colors cursor-pointer"
                        >
                          Side-by-side
                        </button>
                      </div>
                    ))}
                  {state.savedCalculations.filter((c) => c.id !== comparingCalc.id).length === 0 && (
                    <p className="text-xs text-[#9CA3AF] py-3 text-center">
                      Save another calculation in this category to perform a direct side-by-side comparison.
                    </p>
                  )}
                </div>
              </div>
            </div>

            <div className="flex justify-end pt-2">
              <button
                onClick={() => setComparingCalc(null)}
                className="px-4 py-2 bg-[#0F766E] text-white font-bold text-xs rounded-xl shadow-xs cursor-pointer"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
